import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import clsx from "clsx";
import "./BacktestPage.css"; // Ensure CSS is imported
// Import useParams
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
// Removed useAuth0 import - now using unified authentication
import useUser from '../hooks/useUser';
import { Alert, Spinner, Button, Badge } from "react-bootstrap";
import CustomDatePicker from "../components/DatePicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SavedEnvVarSetSelector from "../components/SavedEnvVarSetSelector";
import {
  SAVED_ENV_VAR_SET_SELECTION_KEYS,
  loadSavedEnvVarSetSelection,
  saveSavedEnvVarSetSelection,
} from "../utils/savedEnvVarSetSelection";
import { StrategyActionButton } from "../components/StrategyActions";
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faHistory,
  faCalendarXmark,
  faTimes,
  faDownload,
  faCirclePlay, // Backtest design language: keep circular play icon for Run button
  faExclamationTriangle, // Import warning icon
  faTools,
  faSync, // Import refresh icon
  faLock,
  faCog, // Configuration section icon
} from "@fortawesome/free-solid-svg-icons";
import { MARKET_EVENTS, findMarketEvents, groupEventsByYear, getDefaultDates } from "../utils/marketEvents";
import {
  formatters,
  parseNumericValue,
  makePeriodKey,
  startBacktest,
  stopBacktest,
  getBacktestStatus
} from '../utils/backtestHelpers';
import { fetchWithAuth } from '../utils/apiHelpers';
import { formatCurrencyWithThousands } from '../utils/formatters';
import BacktestTable from "../components/BacktestTable";
import BacktestLogs from "../components/BacktestLogs";
import BacktestStartAnimation from "../components/BacktestStartAnimation";
import ShareStrategyModal from '../components/ShareStrategyModal/ShareStrategyModal';
import MiniChart from '../components/MiniChart';
import BacktestProgress from '../components/BacktestProgress';
import BacktestStatusCard from '../components/BacktestStatusCard/BacktestStatusCard';
import BacktestChartStorage from '../utils/BacktestChartStorage';
import ProviderSelector from "../components/ProviderSelector/ProviderSelector";
import AssetPills from '../components/AssetPills/AssetPills';
import {
  normalizeAssetToken as normalizeRequirement,
  formatAssetTokenLabel as formatRequirementLabel,
  formatAssetList as formatRequirementList,
  REQUIREMENT_DISPLAY_ORDER,
} from '../utils/assetTokens';
// --- NEW: Import interpretErrorCode ---
import { interpretErrorCode } from "../data/errorMessages";
// --- END NEW ---
import { pushEvent, ANALYTICS_EVENTS } from "../utils/analytics";
import { checkoutDebugLog, isCheckoutDebugEnabled } from "../utils/checkoutDebug";
import { createEnhancedSparkleEffect, addEnhancedAnimations } from "../utils/confetti";
import "react-datepicker/dist/react-datepicker.css";
import { ContentModal, PaywallModal } from "../components/UnifiedModal";
import useProductCatalog from "../hooks/useProductCatalog";
import { useCheckoutCart } from "../contexts/CheckoutCartContext";
import { buildProviderOptions } from "../utils/providerCatalog";
import { detectRequiredEnvVars, checkRequiredEnvVarsStatus } from "../utils/codeAnalysis";
import { AI_AGENT_ROUTE, navigateToAiAgent } from "../utils/aiAgentNav";
import {
  NO_TRADES_FALLBACK_MESSAGE,
  normalizeStatusLabel,
  isCompletedStatus,
  isTerminalStatus,
  finalizeCompletedBacktest,
  cleanStatusKey
} from "./backtestStatusHelpers";

const THETA_PROVIDER_SLUG = "theta_data";
const DAY_MS = 24 * 60 * 60 * 1000;
const THETA_TRIAL_LOOKBACK_DAYS = 365;
const GUARDRAIL_MIN_RANGE_DAYS = 14;

const clampToMiddayUTC = (input) => {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setUTCHours(12, 0, 0, 0);
  return date;
};

const calculateDaysBetween = (start, end) => {
  const diff = end.getTime() - start.getTime();
  if (diff <= 0) {
    return 0;
  }
  return Math.ceil(diff / DAY_MS);
};

export const buildBacktestStartParams = ({
  aiStrategyId,
  strategyCode,
  startDate,
  endDate,
  revisionId,
  selectedProvider,
  savedEnvVarSetIds = [],
  linkedStrategyId,
}) => {
  return {
    bot_id: aiStrategyId,
    main: strategyCode,
    requirements: "lumibot",
    start_date: startDate,
    end_date: endDate,
    revisionId,
    dataProvider: selectedProvider,
    ...(savedEnvVarSetIds.length > 0 && { envVarSetIds: savedEnvVarSetIds }),
    ...(linkedStrategyId && { linkedStrategyId }),
  };
};

export const detectThetaPricingIssues = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }
  const issues = [];
  entries.forEach((entry) => {
    if (!entry?.productSlug) {
      return;
    }
    if (!entry.catalogProduct) {
      issues.push({ productSlug: entry.productSlug, reason: "missing_product" });
      return;
    }
    const plans = Array.isArray(entry.plans) ? entry.plans : [];
    if (!plans.length) {
      issues.push({ productSlug: entry.productSlug, reason: "missing_plan" });
      return;
    }
    const hasStripePrice = plans.some(
      (plan) => typeof plan?.stripePriceId === "string" && plan.stripePriceId.trim(),
    );
    if (!hasStripePrice) {
      issues.push({ productSlug: entry.productSlug, reason: "missing_price" });
    }
  });
  return issues;
};

export const sanitizeThetaPlanSelection = (selection, entries) => {
  if (!selection || typeof selection !== "object") {
    return {};
  }
  if (!Array.isArray(entries) || entries.length === 0) {
    return selection;
  }

  const entryBySlug = new Map();
  const thetaSlugs = [];
  const bundleOrder = [];

  entries.forEach((entry) => {
    if (!entry || !entry.productSlug) {
      return;
    }
    entryBySlug.set(entry.productSlug, entry);
    thetaSlugs.push(entry.productSlug);
    if (entry.isBundle) {
      bundleOrder.push(entry.productSlug);
    }
  });

  if (bundleOrder.length === 0) {
    return selection;
  }

  const selectedBundles = bundleOrder.filter((slug) =>
    Object.prototype.hasOwnProperty.call(selection, slug)
  );

  if (selectedBundles.length === 0) {
    return selection;
  }

  const selectedBundleEntries = selectedBundles
    .map((slug) => entryBySlug.get(slug))
    .filter(Boolean);

  const bundleGroups = new Set(
    selectedBundleEntries
      .map((entry) => entry?.exclusivityGroup)
      .filter((group) => typeof group === "string" && group.length > 0)
  );

  const removeAllTheta = selectedBundleEntries.some(
    (entry) => !entry?.exclusivityGroup || !String(entry.exclusivityGroup).length
  );

  const thetaSlugSet = new Set(thetaSlugs);

  let sanitized = null;
  const ensureCopy = () => {
    if (!sanitized) {
      sanitized = { ...selection };
    }
    return sanitized;
  };

  const primaryBundleSlug = selectedBundles[0];

  selectedBundles.slice(1).forEach((slug) => {
    if (Object.prototype.hasOwnProperty.call(selection, slug)) {
      delete ensureCopy()[slug];
    }
  });

  Object.keys(selection).forEach((slug) => {
    if (slug === primaryBundleSlug) return;
    if (!thetaSlugSet.has(slug)) return;
    const entry = entryBySlug.get(slug);
    const entryGroup =
      entry && typeof entry.exclusivityGroup === "string" && entry.exclusivityGroup.length > 0
        ? entry.exclusivityGroup
        : null;
    const sharesGroup = entryGroup ? bundleGroups.has(entryGroup) : false;
    const missingGroupConflict = bundleGroups.size > 0 && !entryGroup;
    const shouldRemove =
      removeAllTheta || sharesGroup || missingGroupConflict || (entry && entry.isBundle);
    if (shouldRemove && Object.prototype.hasOwnProperty.call(selection, slug)) {
      delete ensureCopy()[slug];
    }
  });

  return sanitized || selection;
};

const unrefTimer = (handle) => {
  if (handle && typeof handle.unref === "function") {
    handle.unref();
  }
};

const serializeDateForQuery = (input) => {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const normalizeToDate = (input) => {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (!input) {
    return null;
  }
  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateParam = (value, fallback) => {
  if (typeof value === "string" && value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return normalizeToDate(fallback);
};

const mergeUniqueSlugs = (current = [], incoming = []) => {
  const set = new Set(current);
  incoming.forEach((slug) => {
    if (slug) {
      set.add(slug);
    }
  });
  return Array.from(set);
};

const ACTIVE_BACKTEST_STATUSES = new Set([
  "queued",
  "building",
  "deploying",
  "running",
  "backtesting",
  "initiating",
  "initiated",
  "in progress",
  "stopping",
  "preparing",
  "pending",
  "starting",
  "resuming",
  "connection issue",
]);

const dedupeOrderedTokens = (tokens = []) => {
  const seen = new Set();
  const ordered = [];
  tokens.forEach((token) => {
    const normalized = normalizeRequirement(token);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  });

  if (ordered.length <= 1) return ordered;

  const sorted = [];
  REQUIREMENT_DISPLAY_ORDER.forEach((canonical) => {
    if (ordered.includes(canonical)) {
      sorted.push(canonical);
    }
  });
  ordered.forEach((token) => {
    if (!sorted.includes(token)) {
      sorted.push(token);
    }
  });
  return sorted;
};

export const findCoverageGap = (requirements = [], supported) => {
  if (!Array.isArray(requirements) || requirements.length === 0) {
    return [];
  }

  let supportedSet = new Set();
  if (supported instanceof Set) {
    supportedSet = supported;
  } else if (Array.isArray(supported)) {
    supportedSet = new Set(supported.map((token) => normalizeRequirement(token)).filter(Boolean));
  } else if (typeof supported === "object" && supported !== null) {
    supportedSet = new Set(
      Object.values(supported)
        .flat()
        .map((token) => normalizeRequirement(token))
        .filter(Boolean),
    );
  }

  const normalizedSupported = new Set(Array.from(supportedSet).map((token) => normalizeRequirement(token)));

  const gap = [];
  const seen = new Set();
  requirements.forEach((raw) => {
    const token = normalizeRequirement(raw);
    if (!token || seen.has(token)) return;
    seen.add(token);
    if (!normalizedSupported.has(token)) {
      gap.push(token);
    }
  });
  return gap;
};

const thetaRequirementKeyFromToken = (token) => {
  const normalized = normalizeRequirement(token);
  if (normalized === "stocks" || normalized === "options" || normalized === "indexes") {
    return normalized;
  }
  return null;
};

const thetaCapabilityTagFromToken = (token) => {
  const normalized = normalizeRequirement(token);
  if (normalized === "stocks" || normalized === "options" || normalized === "indexes") {
    return normalized;
  }
  return null;
};

const calculateLookbackDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }
  const diff = endDate.getTime() - startDate.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / DAY_MS);
};

const getEffectiveAccess = (product) => {
  if (!product) return null;
  return product.coverageAccess || product.access || null;
};

const getAccessPriority = (access) => {
  if (!access) return 0;
  if (access.accessType === "paid" || access.accessType === "manual") return 3;
  if (access.accessType === "trial" && access.status === "trial_active") return 1;
  return 0;
};

const hasDirectActiveAccess = (product) => getAccessPriority(product?.access) > 0;

const productHasActiveAccess = (product) => getAccessPriority(getEffectiveAccess(product)) > 0;

const getTrialLookbackLimit = (product) => {
  if (product?.coverageSource?.defaultTrialLookbackDays != null) {
    return product.coverageSource.defaultTrialLookbackDays;
  }
  return product?.defaultTrialLookbackDays ?? null;
};

export const computeAutoRunReady = ({
  shouldAutoRun,
  isInitialSetupLoading,
  isLoading,
  shouldUseTheta,
  thetaAccessLoading,
  thetaRequiredProducts,
}) => {
  if (!shouldAutoRun) return false;
  if (isInitialSetupLoading || isLoading) return false;
  if (shouldUseTheta) {
    if (thetaAccessLoading) return false;
    if (Array.isArray(thetaRequiredProducts) && thetaRequiredProducts.length > 0) {
      return thetaRequiredProducts.every(productHasActiveAccess);
    }
  }
  return true;
};

const describeProductStatus = (product) => {
  const access = getEffectiveAccess(product);
  if (!access) {
    if (product?.coverageSource) {
      return { label: `Covered by ${product.coverageSource.displayName}`, variant: "success" };
    }
    return { label: "Not Active", variant: "secondary" };
  }

  const { accessType, status, trialDaysRemaining } = access;

  if (product?.coverageSource && (accessType === "paid" || accessType === "manual")) {
    return { label: `Covered by ${product.coverageSource.displayName}`, variant: "success" };
  }

  if (accessType === "paid") {
    return { label: "Paid", variant: "success" };
  }

  if (accessType === "manual") {
    return { label: "Granted", variant: "info" };
  }

  if (accessType === "trial" && status === "trial_active") {
    const label = trialDaysRemaining != null
      ? `Trial (${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left)`
      : "Trial Active";
    return { label, variant: "warning" };
  }

  if (accessType === "trial" && status === "trial_expired") {
    return { label: "Trial Expired", variant: "secondary" };
  }

  return { label: "Not Active", variant: "secondary" };
};

/** Main page component **/
export default function BacktestPage() {
  React.useEffect(() => {
    document.body.classList.add('backtesting-page');
    return () => document.body.classList.remove('backtesting-page');
  }, []);

  const { getApiToken } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { aiStrategyId, revisionId } = useParams();

  // LinkedStrategy support - for protected strategies where code stays with author
  const initialLinkedStrategyId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('linkedStrategyId') || null;
  }, [location.search]);
  const [linkedStrategyId, setLinkedStrategyId] = useState(initialLinkedStrategyId);

  const [baseStrategyId, setBaseStrategyId] = useState(null);
  const [baseStrategyIdError, setBaseStrategyIdError] = useState("");
  const [isInitialSetupLoading, setIsInitialSetupLoading] = useState(true);

  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [strategyCode, setStrategyCode] = useState("");
  const [strategyName, setStrategyName] = useState("");
  const [versionNumber, setVersionNumber] = useState("1");
  const [backtestHistory, setBacktestHistory] = useState([]);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const cleanStrategyName = useMemo(() => {
    return strategyName?.replace(/^Backtest:\s*/, '').replace(/\s+v\d+$/, '').trim() || "Untitled Strategy";
  }, [strategyName]);

  const requiredParamsError = useMemo(() => {
    // LinkedStrategy mode - don't require aiStrategyId/revisionId from URL
    if (linkedStrategyId) return null;

    const missing = [];
    if (!aiStrategyId) missing.push("aiStrategyId (from URL path)");
    if (!revisionId) missing.push("revisionId (from URL path)");
    if (baseStrategyIdError) missing.push(`base strategyId (${baseStrategyIdError})`);
    return missing.length > 0 ? `Missing or invalid parameter(s): ${missing.join(", ")}` : null;
  }, [aiStrategyId, revisionId, baseStrategyIdError, linkedStrategyId]);

  const defaultDates = getDefaultDates();

  const [strategyDescription, setStrategyDescription] = useState("");
  // Legacy inline env vars are no longer accepted by the API. Saved vault-backed
  // env var sets are the only user-provided runtime secret path.
  const envVarsAwaitingDecryption = false;
  const [savedEnvVarSetIds, setSavedEnvVarSetIds] = useState(() =>
    loadSavedEnvVarSetSelection(SAVED_ENV_VAR_SET_SELECTION_KEYS.backtest)
  );
  const [savedEnvVarSets, setSavedEnvVarSets] = useState([]);
  const initialSearchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [checkoutStatus, setCheckoutStatus] = useState(() => initialSearchParams.get("checkoutStatus"));
  const [purchasedProductsParam, setPurchasedProductsParam] = useState(() => initialSearchParams.get("purchasedProducts") || "");
  const [providerCatalog, setProviderCatalog] = useState([]);
  // Start as true to prevent configuration warnings from expanding before provider is validated
  // The provider from URL may be invalid and get corrected after catalog loads
  const [providerCatalogLoading, setProviderCatalogLoading] = useState(true);
  const [providerCatalogError, setProviderCatalogError] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(() => initialSearchParams.get("provider") || "botspot_auto");

  useEffect(() => {
    saveSavedEnvVarSetSelection(SAVED_ENV_VAR_SET_SELECTION_KEYS.backtest, savedEnvVarSetIds);
  }, [savedEnvVarSetIds]);
  const selectedSavedEnvVarKeyNames = useMemo(() => {
    const selected = new Set(savedEnvVarSetIds || []);
    return Array.from(new Set(
      savedEnvVarSets
        .filter((item) => selected.has(item.id))
        .flatMap((item) => Array.isArray(item.keyNames) ? item.keyNames : [])
        .filter(Boolean)
    ));
  }, [savedEnvVarSetIds, savedEnvVarSets]);
  const providerOverrideRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorLogsExpanded, setErrorLogsExpanded] = useState(false);
  const [backtestStatus, setBacktestStatus] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimeRef = useRef(0);
  const timerIntervalRef = useRef(null);
  const pollingRef = useRef(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [rangeMode, setRangeMode] = useState("none");
  const [startDate, setStartDate] = useState(() => parseDateParam(initialSearchParams.get("startDate"), defaultDates.start));
  const [endDate, setEndDate] = useState(() => parseDateParam(initialSearchParams.get("endDate"), defaultDates.end));
  const filterButtonRef = useRef(null);
  const [currentBacktestId, setCurrentBacktestId] = useState(null);
  const [shouldAutoRun, setShouldAutoRun] = useState(() => initialSearchParams.get("runBacktest") === "1");

  // Chart state and storage
  const [chartData, setChartData] = useState([]);
  const chartStorageRef = useRef(new BacktestChartStorage());
  const [showStartAnimation, setShowStartAnimation] = useState(false);
  const backtestButtonRef = useRef(null);
  const [liveLogsExpanded, setLiveLogsExpanded] = useState(false); // New state for live logs
  const [showShareModal, setShowShareModal] = useState(false); // State for share modal
  const [isStrategyPublic, setIsStrategyPublic] = useState(false);
  const [isSharingStrategy, setIsSharingStrategy] = useState(false);
  const [showRunConfigDetails, setShowRunConfigDetails] = useState(false);
  const [providerSectionCollapsed, setProviderSectionCollapsed] = useState(true);
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(true);

  // --- NEW: State for required env var status ---
  // forProvider tracks which provider the status was calculated for - prevents stale data race conditions
  const [requiredVarsStatus, setRequiredVarsStatus] = useState({ required: [], missing: [], present: [], invalidLengthDetails: [], forProvider: null });

  // Data provider access tracking
  const [strategyDataRequirements, setStrategyDataRequirements] = useState([]);
  const [strategyAssetClasses, setStrategyAssetClasses] = useState([]);
  const [thetaAccessSummary, setThetaAccessSummary] = useState(null);
  const [thetaAccessLoading, setThetaAccessLoading] = useState(false);
  const [thetaAccessError, setThetaAccessError] = useState(null);

  // Trial + upgrade UI state
  const [pendingTrialProducts, setPendingTrialProducts] = useState([]);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialRequestPending, setTrialRequestPending] = useState(false);
  const [trialModalError, setTrialModalError] = useState(null);
  const runBacktestRef = useRef(null);
  const latestThetaSummaryRef = useRef(null);
  const providerWarningLoggedRef = useRef(false);
  const lastAppliedSearchRef = useRef(location.search);
  const planDebugSeqRef = useRef(0);

  const [showGuardrailModal, setShowGuardrailModal] = useState(false);
  const [guardrailDetails, setGuardrailDetails] = useState(null);

  const [showPlansModal, setShowPlansModal] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState({});

  // PaywallModal state - shown when backtest limit is reached
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallData, setPaywallData] = useState(null);
  const [pendingSelectionSlugs, setPendingSelectionSlugs] = useState([]);
  const [checkoutError, setCheckoutError] = useState(null);
  const {
    catalog,
    loading: catalogLoading,
    error: catalogLoadError,
    refresh: refreshCatalog,
  } = useProductCatalog();
  const { cart, replaceCart, pending: cartPending, error: cartMutationError } = useCheckoutCart();
  const [providerCapabilities, setProviderCapabilities] = useState({});
  const checkoutDebugEnabled = isCheckoutDebugEnabled;

  const normalizedRequirements = useMemo(
    () => {
      // Combine both dataRequirements and assetClasses for coverage checking
      const combined = [
        ...(strategyDataRequirements || []),
        ...(strategyAssetClasses || []),
      ];
      return combined.map(normalizeRequirement).filter(Boolean);
    },
    [strategyDataRequirements, strategyAssetClasses]
  );

  const uniqueRequirementTokens = useMemo(
    () => dedupeOrderedTokens(normalizedRequirements),
    [normalizedRequirements]
  );

  const thetaRequirementKeys = useMemo(() => {
    const unique = new Set();
    normalizedRequirements.forEach((req) => {
      const mapped = thetaRequirementKeyFromToken(req);
      if (mapped) unique.add(mapped);
    });
    return Array.from(unique);
  }, [normalizedRequirements]);

  const thetaCapabilityRequirements = useMemo(() => {
    const unique = new Set();
    normalizedRequirements.forEach((req) => {
      const mapped = thetaCapabilityTagFromToken(req);
      if (mapped) unique.add(mapped);
    });
    return Array.from(unique);
  }, [normalizedRequirements]);

  const hasThetaRequirements = thetaCapabilityRequirements.length > 0;
  const shouldUseTheta =
    (selectedProvider === "theta_data" || selectedProvider === "botspot_auto") && hasThetaRequirements;

  const requirementTokens = useMemo(() => {
    if (thetaRequirementKeys.length > 0) {
      return thetaRequirementKeys;
    }
    return uniqueRequirementTokens;
  }, [thetaRequirementKeys, uniqueRequirementTokens]);

  const requirementsParam = useMemo(
    () => requirementTokens.join(','),
    [requirementTokens]
  );

  const providerOptionsForRender = useMemo(() => {
    const baseOptions = providerCatalog.length
      ? providerCatalog
      : buildProviderOptions([
          { slug: 'botspot_auto', name: 'BotSpot Auto (recommended)' },
          { slug: THETA_PROVIDER_SLUG, name: 'Theta Data' },
        ]);

    const autoEntry = baseOptions.find((provider) => provider.slug === 'botspot_auto');
    const thetaEntry = baseOptions.find((provider) => provider.slug === THETA_PROVIDER_SLUG);
    const customEntry = baseOptions.find((provider) => provider.slug === 'custom');
    const middle = baseOptions.filter(
      (provider) => provider.slug !== 'botspot_auto' && provider.slug !== THETA_PROVIDER_SLUG && provider.slug !== 'custom'
    );

    const ordered = [
      ...(autoEntry ? [autoEntry] : []),
      ...(thetaEntry ? [thetaEntry] : []),
      ...middle,
      ...(customEntry ? [customEntry] : []),
    ];

    return ordered.length ? ordered : baseOptions;
  }, [providerCatalog]);

  useEffect(() => {
    providerOverrideRef.current = false;
  }, [uniqueRequirementTokens.join('|')]);

  const selectedProviderInfo = useMemo(
    () => providerOptionsForRender.find((provider) => provider.slug === selectedProvider) || null,
    [providerOptionsForRender, selectedProvider]
  );

  const unsupportedRequirements = useMemo(() => {
    if (selectedProvider === "custom") {
      return [];
    }
    const supported = providerCapabilities[selectedProvider] || [];
    const gap = findCoverageGap(uniqueRequirementTokens, supported);
    return gap;
  }, [providerCapabilities, selectedProvider, uniqueRequirementTokens]);

  const unsupportedRequirementLabels = useMemo(
    () => unsupportedRequirements.map((token) => formatRequirementLabel(token)),
    [unsupportedRequirements]
  );

  const unsupportedRequirementSummary = useMemo(
    () => formatRequirementList(unsupportedRequirementLabels),
    [unsupportedRequirementLabels]
  );

  const providerCoverageWarning = unsupportedRequirements.length > 0;

  const providerCoverageWarningInfo = useMemo(() => {
    if (!providerCoverageWarning) return null;

    const isThetaUnsupportedCombo =
      selectedProvider === 'theta_data' &&
      (unsupportedRequirements.includes('crypto') || unsupportedRequirements.includes('futures'));

    if (isThetaUnsupportedCombo) {
      const canSwitchToAuto = unsupportedRequirements.includes('crypto');
      return {
        title: `Theta Data does not support ${unsupportedRequirementSummary}.`,
        body: (
          <div>
            <div>This strategy requires {unsupportedRequirementSummary} data.</div>
            <div className="mt-2">
              {canSwitchToAuto ? (
                <Button
                  size="sm"
                  variant="outline-light"
                  onClick={() => {
                    providerOverrideRef.current = true;
                    setSelectedProvider('botspot_auto');
                  }}
                >
                  Switch to BotSpot Auto
                </Button>
              ) : (
                <span>Choose a compatible provider to continue.</span>
              )}
            </div>
          </div>
        ),
      };
    }

    return {
      title: `${selectedProviderInfo?.displayName || selectedProviderInfo?.name || 'Selected provider'} does not support ${unsupportedRequirementSummary}.`,
      body: `This strategy requires ${unsupportedRequirementSummary} data. Please select a data provider that supports ${unsupportedRequirementSummary}.`,
    };
  }, [
    providerCoverageWarning,
    selectedProvider,
    unsupportedRequirements,
    unsupportedRequirementSummary,
    selectedProviderInfo,
  ]);
  const tradingAssetSummary = useMemo(
    () => formatRequirementList(strategyAssetClasses.map((token) => formatRequirementLabel(token))),
    [strategyAssetClasses]
  );

  const deriveThetaProducts = useCallback((summary) => {
    if (!summary || !Array.isArray(summary.products)) return [];
    if (thetaCapabilityRequirements.length === 0) return [];
    const requiredTags = new Set(thetaCapabilityRequirements);
    const map = new Map();
    const activeBundles = summary.products.filter(
      (product) => product?.isBundle && hasDirectActiveAccess(product),
    );

    summary.products.forEach((product) => {
      if (!product || product.isBundle) return;
      const tags = Array.isArray(product.capabilityTags)
        ? product.capabilityTags.map(normalizeRequirement)
        : [];
      const matches = tags.some((tag) => requiredTags.has(tag));
      if (matches) {
        let coverageAccess = null;
        let coverageSource = null;
        const directPriority = getAccessPriority(product.access);

        for (const bundle of activeBundles) {
          const bundleTags = Array.isArray(bundle.capabilityTags)
            ? bundle.capabilityTags.map(normalizeRequirement)
            : [];
          const coversAll = tags.every((tag) => bundleTags.includes(tag));
          const bundlePriority = getAccessPriority(bundle.access);
          if (coversAll && bundlePriority > directPriority) {
            coverageAccess = bundle.access;
            coverageSource = {
              slug: bundle.productSlug,
              displayName: bundle.displayName,
              defaultTrialLookbackDays: bundle.defaultTrialLookbackDays,
            };
            break;
          }
        }

        map.set(product.productSlug, {
          ...product,
          coverageAccess,
          coverageSource,
        });
      }
    });

    return Array.from(map.values());
  }, [thetaCapabilityRequirements]);

  const queueProductSelections = useCallback((products) => {
    if (!Array.isArray(products) || products.length === 0) {
      return;
    }
    const slugs = products
      .map((product) => {
        if (!product) return "";
        if (typeof product === "string") return product;
        return product.productSlug || product.slug || "";
      })
      .filter(Boolean);
    if (slugs.length === 0) {
      return;
    }
    setPendingSelectionSlugs((prev) => mergeUniqueSlugs(prev, slugs));
  }, []);

  const thetaRequiredProducts = useMemo(() => {
    if (!shouldUseTheta) return [];
    return deriveThetaProducts(thetaAccessSummary);
  }, [shouldUseTheta, deriveThetaProducts, thetaAccessSummary]);

  const thetaMissingProducts = useMemo(
    () => (shouldUseTheta ? thetaRequiredProducts.filter((product) => !productHasActiveAccess(product)) : []),
    [shouldUseTheta, thetaRequiredProducts]
  );

  const selectedLookbackDays = useMemo(() => calculateLookbackDays(startDate, endDate), [startDate, endDate]);

  const selectedStartDaysAgo = useMemo(() => {
    if (!startDate) return null;
    const start = clampToMiddayUTC(startDate);
    const today = clampToMiddayUTC(new Date());
    if (!start || !today) return null;
    return calculateDaysBetween(start, today);
  }, [startDate]);

  const thetaTrialRangeConflicts = useMemo(() => {
    if (!shouldUseTheta || !selectedLookbackDays) return [];
    return thetaRequiredProducts.filter((product) => {
      const access = getEffectiveAccess(product);
      const accessType = access?.accessType;
      const status = access?.status;
      const limit = getTrialLookbackLimit(product);
      if (accessType !== "trial" || status !== "trial_active") return false;
      if (!limit) return false;
      return selectedLookbackDays > limit;
    });
  }, [shouldUseTheta, thetaRequiredProducts, selectedLookbackDays]);

  const catalogProductMap = useMemo(() => {
    const map = new Map();
    const products = Array.isArray(catalog?.products) ? catalog.products : [];
    products.forEach((product) => {
      if (product?.slug) {
        map.set(product.slug, product);
      }
    });
    return map;
  }, [catalog]);

  const requiredSlugSet = useMemo(() => {
    const set = new Set();
    thetaRequiredProducts.forEach((product) => {
      if (product?.productSlug) {
        set.add(product.productSlug);
      }
    });
    return set;
  }, [thetaRequiredProducts]);

  const conflictSlugSet = useMemo(() => {
    const set = new Set();
    thetaTrialRangeConflicts.forEach((product) => {
      if (product?.productSlug) {
        set.add(product.productSlug);
      }
    });
    return set;
  }, [thetaTrialRangeConflicts]);

  const selectedPlanSet = useMemo(() => new Set(Object.keys(selectedPlans)), [selectedPlans]);

  const thetaPlanEntries = useMemo(() => {
    if (!thetaAccessSummary || !Array.isArray(thetaAccessSummary.products)) {
      return [];
    }

    const entries = thetaAccessSummary.products.map((product) => {
      const catalogProduct = catalogProductMap.get(product.productSlug) || null;
      const plansFromCatalog = Array.isArray(catalogProduct?.plans) ? catalogProduct.plans : [];
      const metadataPrice = typeof product?.metadata?.priceMonthlyUsd === "number"
        ? product.metadata.priceMonthlyUsd
        : null;

      const plans = plansFromCatalog;
      const defaultPlan = plans.find((plan) => plan.default) || plans[0] || null;
      const exclusivityGroupRaw =
        (catalogProduct?.metadata && typeof catalogProduct.metadata.bundleGroup === "string"
          ? catalogProduct.metadata.bundleGroup
          : null) ||
        (product?.metadata && typeof product.metadata.bundleGroup === "string"
          ? product.metadata.bundleGroup
          : null);
      const exclusivityGroup = exclusivityGroupRaw || null;
      const isSelected = selectedPlanSet.has(product.productSlug);
      const effectiveAccess = getEffectiveAccess(product);
      const accessType = effectiveAccess?.accessType;
      const accessStatus = effectiveAccess?.status;
      const isDisabled = accessType === "paid" || accessType === "manual";
      let disabledReason = null;
      if (product?.coverageSource && productHasActiveAccess(product)) {
        disabledReason = `Covered by ${product.coverageSource.displayName}`;
      } else if (accessType === "paid") {
        disabledReason = "Already active";
      } else if (accessType === "manual") {
        disabledReason = "Provided by support";
      }
      const statusMeta = describeProductStatus(product);
      let priceLabel = null;
      if (defaultPlan && typeof defaultPlan.amount === "number" && defaultPlan.currency) {
        const amount = defaultPlan.amount / 100;
        const formatted = formatCurrencyWithThousands(amount);
        let cadence = "";
        if (defaultPlan.interval === "month") {
          cadence = " / month";
        } else if (defaultPlan.interval === "year") {
          cadence = " / year";
        } else if (defaultPlan.interval) {
          cadence = ` / ${defaultPlan.interval}`;
        }
        priceLabel = `${formatted}${cadence}`;
      } else if (defaultPlan?.label) {
        priceLabel = defaultPlan.label;
      } else if (product?.metadata?.priceMonthlyUsd) {
        priceLabel = `$${product.metadata.priceMonthlyUsd}/month`;
      }

      return {
        product,
        productSlug: product.productSlug,
        displayName: product.displayName,
        capabilitySummary:
          Array.isArray(product.capabilityTags) && product.capabilityTags.length > 0
            ? product.capabilityTags.join(", ")
            : "N/A",
        plans,
        defaultPlan,
        exclusivityGroup,
        catalogProduct,
        isBundle: !!product.isBundle,
        isRequired: requiredSlugSet.has(product.productSlug),
        isConflict: conflictSlugSet.has(product.productSlug),
        isSelected,
        isDisabled,
        disabledReason,
        statusMeta,
        priceLabel,
        trialNote:
          accessType === "trial" &&
          accessStatus === "trial_active" &&
          getTrialLookbackLimit(product)
            ? `Trial lookback limit: ${getTrialLookbackLimit(product)} days`
            : null,
        isBundle: Boolean(product.isBundle),
        isIncludedViaBundle: Boolean(product?.coverageSource),
        bundleInfo: product?.coverageSource
          ? {
              productSlug: product.coverageSource.slug,
              displayName: product.coverageSource.displayName,
            }
          : null,
      };
    });

    const bundleByGroup = new Map();
    entries.forEach((entry) => {
      if (entry.isBundle && entry.exclusivityGroup && selectedPlanSet.has(entry.productSlug)) {
        bundleByGroup.set(entry.exclusivityGroup, entry);
      }
    });

    if (bundleByGroup.size === 0) {
      return entries;
    }

    return entries.map((entry) => {
      if (
        entry.exclusivityGroup &&
        bundleByGroup.has(entry.exclusivityGroup) &&
        bundleByGroup.get(entry.exclusivityGroup).productSlug !== entry.productSlug
      ) {
        const bundleEntry = bundleByGroup.get(entry.exclusivityGroup);
        return {
          ...entry,
          isIncludedViaBundle: true,
          bundleInfo: {
            productSlug: bundleEntry.productSlug,
            displayName: bundleEntry.displayName,
          },
        };
      }
      return entry;
    });
  }, [thetaAccessSummary, catalogProductMap, requiredSlugSet, conflictSlugSet, selectedPlanSet]);

  const thetaPricingIssues = useMemo(
    () => detectThetaPricingIssues(thetaPlanEntries),
    [thetaPlanEntries],
  );

  useEffect(() => {
    if (!thetaRequiredProducts.length) return;
    queueProductSelections(thetaRequiredProducts);
  }, [thetaRequiredProducts, queueProductSelections]);

  useEffect(() => {
    if (!pendingSelectionSlugs.length || !thetaPlanEntries.length) {
      return;
    }
    const entryBySlug = new Map(thetaPlanEntries.map((entry) => [entry.productSlug, entry]));
    const unresolvedSet = new Set();
    let queueApplied = false;
    const appliedChanges = [];

    setSelectedPlans((prev) => {
      let next = { ...prev };

      pendingSelectionSlugs.forEach((slug) => {
        const entry = entryBySlug.get(slug);
        if (!entry) {
          unresolvedSet.add(slug);
          return;
        }

        const plans = Array.isArray(entry.plans) ? entry.plans : [];
        const defaultPlan = entry.defaultPlan || plans[0] || null;
        const targetPlanKey = defaultPlan?.planKey || null;
        if (!targetPlanKey) {
          unresolvedSet.add(slug);
          return;
        }

        if (entry.isBundle) {
          const removedKeys = Object.keys(next).filter((key) => key !== slug);
          if (removedKeys.length > 0) {
            removedKeys.forEach((key) => delete next[key]);
            queueApplied = true;
            appliedChanges.push({
              slug,
              action: "cleared_for_bundle_queue",
              removed: removedKeys.map((key) => ({ productSlug: key })),
            });
          }
        } else {
          thetaPlanEntries.forEach((other) => {
            if (other.productSlug === slug) return;
            const shareGroup =
              (entry.exclusivityGroup && other.exclusivityGroup && other.exclusivityGroup === entry.exclusivityGroup) ||
              other.isBundle;
            if (shareGroup && next[other.productSlug]) {
              delete next[other.productSlug];
              queueApplied = true;
              appliedChanges.push({
                slug: other.productSlug,
                action: "cleared_for_exclusivity_queue",
                exclusivityGroup: entry.exclusivityGroup || other.exclusivityGroup || null,
              });
            }
          });
        }

        if (next[slug] !== targetPlanKey) {
          next[slug] = targetPlanKey;
          queueApplied = true;
          appliedChanges.push({
            slug,
            action: "applied_default",
            planKey: targetPlanKey,
            exclusivityGroup: entry.exclusivityGroup || null,
          });
        }
      });

      const sanitizedResult = sanitizeThetaPlanSelection(next, thetaPlanEntries);
      if (sanitizedResult !== next) {
        const beforeKeys = Object.keys(next);
        const afterKeys = new Set(Object.keys(sanitizedResult));
        const removedSlugs = beforeKeys.filter((key) => !afterKeys.has(key));
        if (removedSlugs.length > 0) {
          appliedChanges.push({
            slug: null,
            action: "sanitized_queue",
            removed: removedSlugs.map((slug) => ({ productSlug: slug })),
          });
        }
        next = sanitizedResult;
        queueApplied = true;
      }

      return queueApplied ? next : prev;
    });

    const unresolved = Array.from(unresolvedSet);
    const unresolvedChanged =
      unresolved.length !== pendingSelectionSlugs.length ||
      unresolved.some((slug, index) => pendingSelectionSlugs[index] !== slug);

    if (unresolvedChanged) {
      setPendingSelectionSlugs(unresolved);
      if (checkoutDebugEnabled) {
        checkoutDebugLog('info', 'planQueue:unresolvedUpdated', {
          pending: pendingSelectionSlugs,
          unresolved,
        });
      }
    }

    if (checkoutDebugEnabled && queueApplied && appliedChanges.length > 0) {
      const debugId = ++planDebugSeqRef.current;
      checkoutDebugLog('info', `planQueue:applied #${debugId}`, {
        appliedChanges,
        remainingQueue: Array.from(unresolvedSet),
      });
    }
  }, [pendingSelectionSlugs, thetaPlanEntries, checkoutDebugEnabled]);

  const providerSlug = thetaAccessSummary?.provider?.slug || THETA_PROVIDER_SLUG;

  const thetaPlanEntriesBySlug = useMemo(() => {
    const map = new Map();
    thetaPlanEntries.forEach((entry) => {
      map.set(entry.productSlug, entry);
    });
    return map;
  }, [thetaPlanEntries]);

  const purchasedProductSlugs = useMemo(() => {
    if (!purchasedProductsParam) {
      return [];
    }
    return purchasedProductsParam
      .split(',')
      .map((slug) => slug.trim())
      .filter(Boolean);
  }, [purchasedProductsParam]);

  const autoRunReady = useMemo(() => computeAutoRunReady({
    shouldAutoRun,
    isInitialSetupLoading,
    isLoading,
    shouldUseTheta,
    thetaAccessLoading,
    thetaRequiredProducts,
  }), [
    shouldAutoRun,
    isInitialSetupLoading,
    isLoading,
    shouldUseTheta,
    thetaAccessLoading,
    thetaRequiredProducts,
  ]);

  const purchasedProductNames = useMemo(() => {
    if (!purchasedProductSlugs.length) {
      return [];
    }
    return purchasedProductSlugs.map((slug) => {
      const entry = thetaPlanEntriesBySlug.get(slug);
      if (entry?.displayName) {
        return entry.displayName;
      }
      return slug
        .split(/[-_]/)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
    });
  }, [purchasedProductSlugs, thetaPlanEntriesBySlug]);

  const [thetaCatalogBlockingError, setThetaCatalogBlockingError] = useState("");
  const thetaCatalogIssueKeyRef = useRef("");

  useEffect(() => {
    if (catalogLoading) {
      return;
    }

    if (!thetaPricingIssues.length) {
      if (thetaCatalogBlockingError) {
        setThetaCatalogBlockingError("");
      }
      thetaCatalogIssueKeyRef.current = "";
      return;
    }

    const issueKey = thetaPricingIssues
      .map((issue) => `${issue.productSlug}:${issue.reason}`)
      .sort()
      .join("|");

    setThetaCatalogBlockingError(
      "We couldn't load Theta Data pricing right now. Please refresh and try again.",
    );

    if (thetaCatalogIssueKeyRef.current !== issueKey) {
      const missingSlugs = Array.from(
        new Set(thetaPricingIssues.map((issue) => issue.productSlug)),
      ).join(",");
      pushEvent({
        event: ANALYTICS_EVENTS.CHECKOUT_CATALOG_MISSING_PRODUCT,
        missingSlugs,
        flow_context: "backtest_data_access",
        cart_item_count: thetaPricingIssues.length,
      });
      thetaCatalogIssueKeyRef.current = issueKey;
    }
  }, [catalogLoading, thetaPricingIssues, thetaCatalogBlockingError]);

  const { selectedPlanItems, selectedPlanCount } = useMemo(() => {
    const items = Object.entries(selectedPlans).reduce((acc, [productSlug, planKey]) => {
      const entry = thetaPlanEntriesBySlug.get(productSlug) || null;
      const plans = Array.isArray(entry?.plans) ? entry.plans : [];
      const matchedPlan =
        plans.find((plan) => plan.planKey === planKey) ||
        entry?.defaultPlan ||
        null;

      if (!matchedPlan) {
        return acc;
      }

      const unitAmount = typeof matchedPlan.amount === "number" ? Math.round(matchedPlan.amount) : null;
      const currency = matchedPlan?.currency ? String(matchedPlan.currency).toUpperCase() : null;
      const interval = matchedPlan?.interval || null;
      const intervalCount =
        typeof matchedPlan?.intervalCount === "number" ? matchedPlan.intervalCount : interval ? 1 : null;
      const planLabel = matchedPlan?.label || planKey;

      acc.push({
        productSlug,
        planKey,
        unitAmount,
        currency,
        interval,
        intervalCount,
        planLabel,
        providerSlug,
      });
      return acc;
    }, []);
    return {
      selectedPlanItems: items,
      selectedPlanCount: items.length,
    };
  }, [providerSlug, selectedPlans, thetaPlanEntriesBySlug]);

useEffect(() => {
  if (!showPlansModal) return;
  if (!cart || !Array.isArray(cart.items)) return;
  if (!thetaPlanEntries.length) return;
  if (selectedPlanCount > 0) return;

  const availableSlugs = new Set(thetaPlanEntries.map((entry) => entry.productSlug));
  const fromCart = cart.items.reduce((acc, item) => {
    const slug = item?.productSlug;
    const planKey = item?.planKey;
    if (slug && planKey && availableSlugs.has(slug)) {
      acc[slug] = planKey;
    }
    return acc;
  }, {});

  if (Object.keys(fromCart).length === 0) {
    if (checkoutDebugEnabled) {
      checkoutDebugLog('info', 'planSync:cartEmpty', { source: 'cart', itemsInCart: cart.items.length });
    }
    return;
  }

  let debugPayload = null;
  setSelectedPlans((prev) => {
    if (Object.keys(prev).length > 0) {
      return prev;
    }
    const sanitized = sanitizeThetaPlanSelection(fromCart, thetaPlanEntries);
    if (checkoutDebugEnabled) {
      const summarize = (plans) =>
        Object.entries(plans || {}).map(([productSlug, planKey]) => ({ productSlug, planKey }));
      debugPayload = {
        trigger: 'hydrateFromCart',
        before: [],
        after: summarize(sanitized),
      };
      if (sanitized !== fromCart) {
        const original = summarize(fromCart);
        const sanitizedRemoved = original.filter(
          (item) =>
            !debugPayload.after.some(
              (finalItem) => finalItem.productSlug === item.productSlug
            )
        );
        if (sanitizedRemoved.length > 0) {
          debugPayload.sanitizedRemoved = sanitizedRemoved;
        }
        debugPayload.sanitized = true;
        debugPayload.original = original;
      }
    }
    return sanitized;
  });

  if (checkoutDebugEnabled && debugPayload) {
    const debugId = ++planDebugSeqRef.current;
    checkoutDebugLog('info', `planSync:event #${debugId}`, debugPayload);
  }
}, [showPlansModal, cart, thetaPlanEntries, selectedPlanCount, checkoutDebugEnabled]);

  const buildBacktestAnalyticsPayload = useCallback(
    (additional = {}) => {
      const base = {
        aiStrategyId,
        ai_strategy_id: aiStrategyId,
        revisionId,
        revision_id: revisionId,
        strategyName: cleanStrategyName,
        strategy_name: cleanStrategyName,
        event_source: "frontend",
        source: "frontend"
      };

      if (baseStrategyId) {
        base.baseStrategyId = baseStrategyId;
        base.base_strategy_id = baseStrategyId;
      }

      const combined = { ...base, ...additional };

      if (combined.backtestId && combined.backtest_id === undefined) {
        combined.backtest_id = combined.backtestId;
      }
      if (combined.managerBotId && combined.manager_bot_id === undefined) {
        combined.manager_bot_id = combined.managerBotId;
      }

      const keyMappings = [
        ["envVarsCount", "env_vars_count"],
        ["errorType", "error_type"],
        ["errorMessage", "error_message"],
        ["failureStage", "failure_stage"],
        ["failureReason", "failure_reason"],
        ["durationMs", "duration_ms"],
        ["durationSeconds", "duration_seconds"],
        ["totalReturn", "total_return"],
        ["maxDrawdown", "max_drawdown"],
        ["sharpe", "sharpe"],
        ["hasTrades", "has_trades"],
        ["newStartDate", "new_start_date"],
        ["newEndDate", "new_end_date"],
        ["logType", "log_type"],
        ["rangeMode", "range_mode"],
        ["selectedBacktestId", "selected_backtest_id"],
        ["errorCode", "error_code"],
        ["statusText", "status_text"],
      ];

      keyMappings.forEach(([camel, snake]) => {
        if (Object.prototype.hasOwnProperty.call(combined, camel) && combined[snake] === undefined) {
          combined[snake] = combined[camel];
        }
      });

      if (combined.duration_ms === undefined && combined.durationSeconds !== undefined) {
        combined.duration_ms = Math.round(Number(combined.durationSeconds) * 1000);
      }

      if (combined.durationSeconds === undefined && combined.duration_ms !== undefined) {
        combined.durationSeconds = Math.round(Number(combined.duration_ms) / 1000);
        combined.duration_seconds = combined.durationSeconds;
      }

      if (combined.startDate && combined.start_date === undefined) {
        combined.start_date = combined.startDate;
      }
      if (combined.endDate && combined.end_date === undefined) {
        combined.end_date = combined.endDate;
      }

      if (!combined.timestamp) {
        combined.timestamp = new Date().toISOString();
      }

      return combined;
    },
    [aiStrategyId, revisionId, baseStrategyId, cleanStrategyName]
  );

  const emitBacktestEvent = useCallback(
    (eventConst, additional = {}) => {
      pushEvent({
        event: eventConst,
        ...buildBacktestAnalyticsPayload(additional)
      });
    },
    [buildBacktestAnalyticsPayload]
  );

  const buildReturnUrl = useCallback(
    (options = {}) => {
      const params = new URLSearchParams(location.search);
      if (startDate) {
        params.set("startDate", serializeDateForQuery(startDate));
      }
      if (endDate) {
        params.set("endDate", serializeDateForQuery(endDate));
      }
      if (baseStrategyId) {
        params.set("strategyId", baseStrategyId);
      }
      if (aiStrategyId) {
        params.set("aiStrategyId", aiStrategyId);
      }
      if (revisionId) {
        params.set("revisionId", revisionId);
      }
      if (selectedProvider) {
        params.set("provider", selectedProvider);
      }
      if (options.autoRun) {
        params.set("runBacktest", "1");
      } else {
        params.delete("runBacktest");
      }
      if (options.checkoutStatus) {
        params.set("checkoutStatus", options.checkoutStatus);
      } else {
        params.delete("checkoutStatus");
      }
      if (Array.isArray(options.purchasedProducts) && options.purchasedProducts.length > 0) {
        params.set("purchasedProducts", options.purchasedProducts.join(","));
      } else {
        params.delete("purchasedProducts");
      }
      const query = params.toString();
      return `${location.pathname}${query ? `?${query}` : ""}`;
    },
    [location.pathname, location.search, startDate, endDate, baseStrategyId, aiStrategyId, revisionId, selectedProvider],
  );

  const handleCheckout = useCallback(async () => {
    if (thetaCatalogBlockingError) {
      setCheckoutError(thetaCatalogBlockingError);
      return;
    }
    if (!selectedPlanItems.length) {
      setCheckoutError("Select at least one plan to continue.");
      return;
    }
    setCheckoutError(null);
    try {
      const providerSlug = thetaAccessSummary?.provider?.slug || THETA_PROVIDER_SLUG;
      const items = selectedPlanItems.map((item) => ({
        productSlug: item.productSlug,
        planKey: item.planKey,
        quantity: 1,
      }));
      const purchasedSlugs = items.map((item) => item.productSlug);
      const returnUrl = buildReturnUrl({
        autoRun: true,
        checkoutStatus: "success",
        purchasedProducts: purchasedSlugs,
      });
      const originMeta = {
        type: "backtest",
        returnUrl,
        providerSlug,
        checkoutStatus: "success",
        purchasedProducts: purchasedSlugs.join(","),
      };
      if (aiStrategyId) originMeta.aiStrategyId = aiStrategyId;
      if (revisionId) originMeta.revisionId = revisionId;
      if (baseStrategyId) originMeta.strategyId = baseStrategyId;
      const metadata = {
        origin: originMeta,
        requestedProducts: selectedPlanItems.map((item) => ({
          providerSlug,
          productSlug: item.productSlug,
          planKey: item.planKey,
          unitAmount: typeof item.unitAmount === "number" ? item.unitAmount : undefined,
          currency: item.currency || undefined,
          interval: item.interval || undefined,
          intervalCount: typeof item.intervalCount === "number" ? item.intervalCount : undefined,
          label: item.planLabel || undefined,
        })),
        leadContext: null,
      };

      if (checkoutDebugEnabled) {
        const debugId = ++planDebugSeqRef.current;
        checkoutDebugLog('info', `checkoutSync:start #${debugId}`, {
          selectionCount: items.length,
          origin: originMeta,
          requestedProducts: metadata.requestedProducts.map((entry) => ({
            providerSlug: entry.providerSlug,
            productSlug: entry.productSlug,
            planKey: entry.planKey,
          })),
        });
      }

      await replaceCart(items, {
        flowContext: "backtest_data_access",
        metadata,
      });
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_DATA_PLAN_CHECKOUT_STARTED, {
        aiStrategyId,
        revisionId,
        baseStrategyId,
        providerSlug,
        selectionCount: items.length,
        products: items.map((item) => item.productSlug),
      });
      navigate("/checkout");
    } catch (checkoutErr) {
      console.error("[BacktestPage] Failed to prepare checkout", checkoutErr);
      if (checkoutDebugEnabled) {
        checkoutDebugLog('warn', 'checkoutSync:error', {
          message: checkoutErr?.message || 'unknown error',
          status: checkoutErr?.status ?? null,
        });
      }
      setCheckoutError(checkoutErr?.message || "Failed to update cart. Please try again.");
    }
  }, [
    selectedPlanItems,
    thetaAccessSummary,
    buildReturnUrl,
    aiStrategyId,
    revisionId,
    baseStrategyId,
    replaceCart,
    navigate,
    emitBacktestEvent,
    checkoutDebugEnabled,
    thetaCatalogBlockingError,
  ]);

  useEffect(() => {
    if (location.search === lastAppliedSearchRef.current) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const nextStart = parseDateParam(params.get("startDate"), defaultDates.start);
    const nextEnd = parseDateParam(params.get("endDate"), defaultDates.end);
    const nextProvider = params.get("provider") || "botspot_auto";
    const nextAutoRun = params.get("runBacktest") === "1";
    const nextCheckoutStatus = params.get("checkoutStatus");
    const nextPurchased = params.get("purchasedProducts") || "";

    setStartDate((prev) => {
      if (
        prev instanceof Date &&
        nextStart instanceof Date &&
        prev.getTime() === nextStart.getTime()
      ) {
        return prev;
      }
      return nextStart;
    });
    setEndDate((prev) => {
      if (
        prev instanceof Date &&
        nextEnd instanceof Date &&
        prev.getTime() === nextEnd.getTime()
      ) {
        return prev;
      }
      return nextEnd;
    });
    setSelectedProvider((prev) => (prev === nextProvider ? prev : nextProvider));
    setCheckoutStatus((prev) =>
      prev === nextCheckoutStatus ? prev : nextCheckoutStatus,
    );
    setPurchasedProductsParam((prev) =>
      prev === nextPurchased ? prev : nextPurchased,
    );
    setShouldAutoRun((prev) => (prev === nextAutoRun ? prev : nextAutoRun));
    lastAppliedSearchRef.current = location.search;
  }, [location.search, defaultDates.start, defaultDates.end]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    ["startDate", "endDate", "strategyId", "aiStrategyId", "revisionId", "provider", "runBacktest", "checkoutStatus", "purchasedProducts"].forEach((key) => {
      params.delete(key);
    });
    if (startDate) {
      params.set("startDate", serializeDateForQuery(startDate));
    }
    if (endDate) {
      params.set("endDate", serializeDateForQuery(endDate));
    }
    if (baseStrategyId) {
      params.set("strategyId", baseStrategyId);
    }
    if (aiStrategyId) {
      params.set("aiStrategyId", aiStrategyId);
    }
    if (revisionId) {
      params.set("revisionId", revisionId);
    }
    if (selectedProvider) {
      params.set("provider", selectedProvider);
    }
    if (shouldAutoRun) {
      params.set("runBacktest", "1");
    }
    const queryString = params.toString();
    const nextSearch = queryString ? `?${queryString}` : "";
    if (nextSearch === location.search) {
      lastAppliedSearchRef.current = location.search;
      return;
    }
    lastAppliedSearchRef.current = nextSearch;
    navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
  }, [
    startDate,
    endDate,
    baseStrategyId,
    aiStrategyId,
    revisionId,
    selectedProvider,
    shouldAutoRun,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Initialize confetti animations on component mount
  useEffect(() => {
    addEnhancedAnimations();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadProviders = async () => {
      if (!aiStrategyId) return;
      setProviderCatalogLoading(true);
      setProviderCatalogError(null);

      try {
        const token = await getApiToken();
        // Don't filter by requirements - always show all providers so users can select any (including Databento for futures)
        const query = '?includeProducts=true';
        const response = await fetchWithAuth(token, `/data-providers${query}`);
        const rawProviders = Array.isArray(response?.providers) ? response.providers : [];
        const providers = buildProviderOptions(rawProviders).filter((provider) => provider.enabled !== false);
        const capabilityMap = providers.reduce((acc, provider) => {
          const supported = Array.isArray(provider?.capabilities?.supported)
            ? provider.capabilities.supported.map(normalizeRequirement).filter(Boolean)
            : [];
          acc[provider.slug] = dedupeOrderedTokens(supported);
          return acc;
        }, {});

        const providerSlugs = providers.map((provider) => provider.slug);
        let nextSelection = selectedProvider;

        if (nextSelection !== "custom" && providerSlugs.length > 0 && !providerSlugs.includes(nextSelection)) {
          nextSelection = providerSlugs[0];
          providerOverrideRef.current = false;
        }

        if (!isMounted) return;
        setProviderCatalog(providers);
        setProviderCapabilities(capabilityMap);

        if (nextSelection !== selectedProvider) {
          setSelectedProvider(nextSelection);
        }
      } catch (err) {
        console.error('[BacktestPage] Failed to load provider catalog:', err);
        if (isMounted) {
          setProviderCatalogError(err?.message || 'Failed to load data providers.');
        }
      } finally {
        if (isMounted) {
          setProviderCatalogLoading(false);
        }
      }
    };

    loadProviders();
    return () => {
      isMounted = false;
    };
  }, [aiStrategyId, getApiToken, selectedProvider, uniqueRequirementTokens]);

  useEffect(() => {
    if (!shouldUseTheta) {
      if (thetaAccessSummary !== null) {
        setThetaAccessSummary(null);
        latestThetaSummaryRef.current = null;
      }
      if (thetaAccessError) {
        setThetaAccessError(null);
      }
      return;
    }

    let isMounted = true;

    const loadAccessSummary = async () => {
      try {
        setThetaAccessLoading(true);
        setThetaAccessError(null);
        const token = await getApiToken();
        const summary = await fetchWithAuth(token, `/data-providers/access?provider=${THETA_PROVIDER_SLUG}`);
        if (isMounted) {
          setThetaAccessSummary(summary);
          latestThetaSummaryRef.current = summary;
        }
      } catch (err) {
        if (isMounted) {
          console.error('[BacktestPage] Failed to load Theta Data access summary:', err);
          setThetaAccessError(err?.message || 'Failed to load Theta Data access summary.');
        }
      } finally {
        if (isMounted) {
          setThetaAccessLoading(false);
        }
      }
    };

    loadAccessSummary();

    return () => {
      isMounted = false;
    };
  }, [shouldUseTheta, getApiToken]);

  useEffect(() => {
    latestThetaSummaryRef.current = thetaAccessSummary;
  }, [thetaAccessSummary]);

  // --- REMOVED: needsPolygon calculation ---
  // const needsPolygon = useMemo(() => { ... }, [strategyCode]);

  // --- REMOVED: Effect to find polygon key ---
  // useEffect(() => { ... }, [savedEnvVarSetIds]);

  // --- UPDATED: isRunDisabled check ---
  const isRunDisabled = useMemo(() => {
    const checkStartDate = !startDate;
    const checkEndDate = !endDate;
    const datesInvalid = startDate && endDate && new Date(startDate) >= new Date(endDate);
    // --- NEW: Check for missing required env vars ---
    const requiredVarsMissing = requiredVarsStatus.missing.length > 0;
    // --- FIX: Check if strategy code is still loading or missing ---
    const strategyCodeMissing = !strategyCode || strategyCode.trim() === '';
    const providerUnsupported = providerCoverageWarning;

    const accessLoading = thetaAccessLoading && shouldUseTheta;

    if (isLoading) return true;
    if (accessLoading) return true;
    if (checkStartDate) return true;
    if (checkEndDate) return true;
    if (datesInvalid) return true;
    if (requiredVarsMissing) return true; // Check specifically for missing required vars
    if (strategyCodeMissing) return true; // Prevent clicking before strategy code loads
    if (providerUnsupported) return true; // Disable button - selected provider doesn't support strategy requirements

    return false; // Otherwise, enable the button
  }, [isLoading, startDate, endDate, requiredVarsStatus, strategyCode, thetaAccessLoading, shouldUseTheta, providerCoverageWarning]); // Add dependencies

  const statusLabel = useMemo(() => {
    if (!backtestStatus) return "";
    if (typeof backtestStatus === "string") {
      return backtestStatus;
    }
    if (typeof backtestStatus === "object" && backtestStatus !== null) {
      return backtestStatus.status || "";
    }
    return "";
  }, [backtestStatus]);

  const isBacktestActive = useMemo(() => {
    if (!isLoading) return false;
    const normalizedStatus = statusLabel.toLowerCase();
    if (ACTIVE_BACKTEST_STATUSES.has(normalizedStatus)) {
      return true;
    }
    if (normalizedStatus && ["completed", "failed", "stopped", "force_stopped", "terminated_timeout"].includes(normalizedStatus)) {
      return false;
    }
    return Boolean(currentBacktestId);
  }, [isLoading, statusLabel, currentBacktestId]);

  useEffect(() => {
    if (!isBacktestActive && showRunConfigDetails) {
      setShowRunConfigDetails(false);
    }
  }, [isBacktestActive, showRunConfigDetails]);

  const handleTogglePlan = useCallback((productSlug) => {
    if (!productSlug) return;
    const entry = thetaPlanEntriesBySlug.get(productSlug);
    if (!entry) {
      if (checkoutDebugEnabled) {
        checkoutDebugLog('warn', 'planToggle:missingEntry', { productSlug });
      }
      return;
    }
    if (entry.isDisabled) {
      if (checkoutDebugEnabled) {
        checkoutDebugLog('info', 'planToggle:disabledEntry', { productSlug });
      }
      return;
    }

    setPendingSelectionSlugs([]);

    const providerSlug = thetaAccessSummary?.provider?.slug || THETA_PROVIDER_SLUG;
    let analyticsPayload = null;
    let debugPayload = null;
    const selectionSummary = (plans) => (
      Object.entries(plans || {}).map(([slug, planKey]) => ({ productSlug: slug, planKey }))
    );

    setSelectedPlans((prev) => {
      const next = { ...prev };
      const previousPlanKey = prev[productSlug] || null;
      const wasSelected = Boolean(previousPlanKey);
      const isBundle = entry.isBundle;
      const beforeSummary = selectionSummary(prev);

      const finalizeSelection = (working) => {
        const finalSelection = sanitizeThetaPlanSelection(working, thetaPlanEntries);
        const finalSummary = selectionSummary(finalSelection);
        const finalCount = finalSummary.length;

        if (analyticsPayload) {
          analyticsPayload.selectionCount = finalCount;
        }

        if (checkoutDebugEnabled && debugPayload) {
          debugPayload.after = finalSummary;
          debugPayload.selectionCount = finalCount;
          if (finalSelection !== working) {
            const workingSummary = selectionSummary(working);
            const sanitizedRemoved = workingSummary.filter(
              (item) =>
                !finalSummary.some(
                  (finalItem) => finalItem.productSlug === item.productSlug
                )
            );
            if (sanitizedRemoved.length > 0) {
              debugPayload.sanitizedRemoved = sanitizedRemoved;
            }
            debugPayload.sanitized = true;
          }
        }

        return finalSelection;
      };

      if (wasSelected) {
        delete next[productSlug];
        analyticsPayload = {
          planKey: previousPlanKey,
          action: "removed",
          bundleGroup: entry.exclusivityGroup || null,
        };
        if (checkoutDebugEnabled) {
          debugPayload = {
            trigger: 'removed',
            productSlug,
            planKey: previousPlanKey,
            bundleGroup: entry.exclusivityGroup || null,
            before: beforeSummary,
          };
        }
        return finalizeSelection(next);
      }

      const plans = Array.isArray(entry.plans) ? entry.plans : [];
      const defaultPlan = entry.defaultPlan || plans[0] || null;
      const planKeyToApply = defaultPlan?.planKey || null;
      if (!planKeyToApply) {
        analyticsPayload = null;
        if (checkoutDebugEnabled) {
          debugPayload = {
            trigger: 'noop_missing_plan',
            productSlug,
            bundleGroup: entry.exclusivityGroup || null,
            before: beforeSummary,
          };
        }
        return prev;
      }

      const removedItems = [];
      if (isBundle) {
        Object.keys(next).forEach((key) => {
          if (key !== productSlug) {
            removedItems.push({ productSlug: key, planKey: next[key] });
            delete next[key];
          }
        });
      } else {
        thetaPlanEntries.forEach((other) => {
          if (other.productSlug === productSlug) return;
          const conflicts =
            (other.exclusivityGroup && other.exclusivityGroup === entry.exclusivityGroup) || other.isBundle;
          if (conflicts && next[other.productSlug]) {
            removedItems.push({ productSlug: other.productSlug, planKey: next[other.productSlug] });
            delete next[other.productSlug];
          }
        });
      }

      next[productSlug] = planKeyToApply;
      analyticsPayload = {
        planKey: planKeyToApply,
        action: "selected",
        bundleGroup: entry.exclusivityGroup || null,
      };
      if (checkoutDebugEnabled) {
        debugPayload = {
          trigger: isBundle ? 'selected_bundle' : removedItems.length ? 'cleared_conflict' : 'selected',
          productSlug,
          planKey: planKeyToApply,
          bundleGroup: entry.exclusivityGroup || null,
          before: beforeSummary,
          removed: removedItems,
        };
      }
      return finalizeSelection(next);
    });

    if (checkoutDebugEnabled && debugPayload) {
      const debugId = ++planDebugSeqRef.current;
      checkoutDebugLog('info', `planToggle:event #${debugId}`, debugPayload);
    }

    if (analyticsPayload) {
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_DATA_PLAN_SELECTION_CHANGED, {
        aiStrategyId,
        revisionId,
        baseStrategyId,
        providerSlug,
        productSlug,
        planKey: analyticsPayload.planKey,
        action: analyticsPayload.action,
        bundleGroup: analyticsPayload.bundleGroup,
        selectionCount: analyticsPayload.selectionCount,
      });
    }
  }, [
    thetaPlanEntriesBySlug,
    thetaPlanEntries,
    thetaAccessSummary,
    emitBacktestEvent,
    aiStrategyId,
    revisionId,
    baseStrategyId,
    checkoutDebugEnabled,
  ]);

  // Compute required env vars from selected saved env var sets even when Settings is collapsed.

  useEffect(() => {
    const requiredKeys = detectRequiredEnvVars(strategyCode || '', 'backtesting', selectedProvider);
    const selectedEnvEntries = selectedSavedEnvVarKeyNames.map((key) => ({
      key,
      value: 'saved-env-var-set',
      savedSecretReference: true,
    }));
    const status = checkRequiredEnvVarsStatus(requiredKeys, selectedEnvEntries);
    // Include which provider this status was calculated for - prevents race conditions
    // where stale status from old provider triggers auto-expand incorrectly
    setRequiredVarsStatus({ ...status, forProvider: selectedProvider });
  }, [strategyCode, selectedProvider, selectedSavedEnvVarKeyNames]);

  // Track when data has fully loaded (provider capabilities from API)
  // Use state (not ref) so that changes trigger re-renders and effects re-run
  const providerCapabilitiesLoaded = Object.keys(providerCapabilities).length > 0;
  const [dataInitialized, setDataInitialized] = useState(false);

  useEffect(() => {
    // Mark as initialized once we have provider capabilities loaded
    // This triggers a re-render so the auto-expand effect can properly evaluate
    if (providerCapabilitiesLoaded && !dataInitialized) {
      setDataInitialized(true);
    }
  }, [providerCapabilitiesLoaded, dataInitialized]);

  // Fallback: set initialized after a delay to handle edge cases
  // (e.g., if provider capabilities API fails or takes too long)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!dataInitialized) {
        setDataInitialized(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [dataInitialized]);

  // Auto-expand the config section when there's a coverage warning or missing env vars
  // CRITICAL: Only consider hasMissingEnvVars true when:
  // 1. There are actually missing keys AND
  // 2. The requiredVarsStatus was calculated for the CURRENT provider
  // This prevents a race condition where:
  // - Provider changes from theta_data to databento
  // - providerCatalogLoading becomes false
  // - But requiredVarsStatus is still stale (calculated for theta_data)
  // - hasMissingEnvVars would be true for the wrong provider, causing panel to expand
  const requiredVarsStatusIsForCurrentProvider = requiredVarsStatus.forProvider === selectedProvider;
  const hasMissingEnvVars = requiredVarsStatus.missing.length > 0 && requiredVarsStatusIsForCurrentProvider;

  // IMPORTANT: Only calculate shouldAutoExpandSettings after data is initialized AND provider is validated
  // This prevents false positives from:
  // - Empty providerCapabilities on initial render
  // - Wrong provider from URL that gets corrected after catalog loads
  // - Saved env var set metadata that has not loaded yet
  // The providerCatalogLoading check ensures we don't expand based on missing keys for the wrong provider
  // The envVarsAwaitingDecryption flag is retained for compatibility with the old effect shape.
  const shouldAutoExpandSettings = dataInitialized && !providerCatalogLoading && !envVarsAwaitingDecryption && (providerCoverageWarning || hasMissingEnvVars);

  useEffect(() => {
    // Don't do anything until data has been initialized
    // This prevents false positives on initial page load due to race conditions
    if (!dataInitialized) {
      return;
    }

    if (!shouldAutoExpandSettings) {
      providerWarningLoggedRef.current = false;
      return;
    }

    // Auto-expand the config section when there's a coverage warning or missing env vars
    setConfigSectionCollapsed(false);

    // Scroll the settings section into view after a brief delay to ensure it's expanded
    const scrollTimer = setTimeout(() => {
      const configSection = document.querySelector('.config-section');
      if (configSection) {
        configSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Only log analytics for provider coverage warnings (not missing env vars)
    if (providerCoverageWarning && !providerWarningLoggedRef.current) {
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_PROVIDER_WARNING_SHOWN, {
        providerSlug: selectedProvider,
        missingRequirements: unsupportedRequirements,
      });
      providerWarningLoggedRef.current = true;
    }

    return () => clearTimeout(scrollTimer);
  }, [emitBacktestEvent, dataInitialized, shouldAutoExpandSettings, providerCoverageWarning, hasMissingEnvVars, selectedProvider, unsupportedRequirements, providerCatalogLoading, envVarsAwaitingDecryption]);

  // Function to highlight and scroll to the expanded card
  function handleSelectBacktest(id) {
    setExpandedCardId(id);
    // --- Analytics: BACKTEST_HISTORY_ITEM_SELECTED ---
    emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_HISTORY_ITEM_SELECTED, {
      selectedBacktestId: id,
      backtestId: id
    });
    const highlightHandle = setTimeout(() => {
      const expandedCard = document.getElementById('expanded-card');
      if (expandedCard) {
        expandedCard.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
        expandedCard.classList.add('highlight-animation');
        const removeHighlightHandle = setTimeout(() => {
          expandedCard.classList.remove('highlight-animation');
        }, 2000);
        unrefTimer(removeHighlightHandle);
      }
    }, 100);
    unrefTimer(highlightHandle);
  }

  /** Extract strategy details from code if any **/
  const parseStrategyDetails = useCallback((code) => {
    if (!code) return;
    const match = code.match(
      /Strategy Description\s*[-]+\s*([\s\S]*?)(?=\n\nUser Query|$)/
    );
    if (match) {
      let desc = match[1].trim();
      desc += "\n\nAdditional notes: carefully check date range.";
      setStrategyDescription(desc);
    }
  }, []);

  /** Timers and polling controls **/
const startTimer = useCallback((initial = 0) => {
  elapsedTimeRef.current = initial;
  setElapsedTime(initial);
  
  // Clear any existing interval
  if (timerIntervalRef.current) {
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = null;
  }
  
  // Start a new interval that increments every second
  timerIntervalRef.current = setInterval(() => {
    elapsedTimeRef.current += 1;
    setElapsedTime(elapsedTimeRef.current);
  }, 1000);
  unrefTimer(timerIntervalRef.current);
}, []);

const stopTimer = useCallback(() => {
  if (timerIntervalRef.current) {
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = null;
  }
  elapsedTimeRef.current = 0;
  setElapsedTime(0);
}, []);

const stopPolling = useCallback(() => {
  if (pollingRef.current) {
    clearInterval(pollingRef.current);
    pollingRef.current = null;
  }
}, []);

useEffect(() => {
  return () => {
    stopTimer();
    stopPolling();
  };
}, [stopPolling, stopTimer]);

  /** Create a single consistent function to normalize status display */
  const normalizeBacktestStatus = useCallback(
    (status, stage, isRunning) => normalizeStatusLabel(status, stage, isRunning),
    []
  );

  /** updateBacktestStatus depends on stopTimer and stopPolling */
  const updateBacktestStatus = useCallback((status, err = "") => {
    setBacktestStatus(status);
    setError(err);
    setIsLoading(!["completed", "failed", "force_stopped", "terminated_timeout"].includes(status));
    if (["completed", "failed", "force_stopped", "terminated_timeout"].includes(status)) {
      stopTimer();
      stopPolling();
      setIsLoading(false);
    }
  }, [stopTimer, stopPolling]);

  /** fetchBacktestHistory should use baseStrategyId */
  const fetchBacktestHistory = useCallback(async (retryCount = 0) => {
    // --- Wait for baseStrategyId ---
    if (!baseStrategyId) {
      console.log("[fetchBacktestHistory] Waiting for baseStrategyId...");
      return;
    }
    // ---

    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s

    try {
      console.log("Fetching backtest history for baseStrategyId:", baseStrategyId); // Updated log
      setIsLoadingHistory(true);
      setHistoryError("");
      const token = await getApiToken();

      // Update URL to use baseStrategyId
      const url = `/backtests/${baseStrategyId}/stats`; // Use baseStrategyId
      console.log("Fetching from URL:", url);
      
      const data = await fetchWithAuth(token, url);
      console.log("Raw backtest history response:", data);
      
      if (!data?.backtests) {
        console.warn("No backtests array in response:", data);
        setBacktestHistory([]);
        return;
      }

      // Log processing
      console.log("Total backtests received:", data.backtests.length);

      const processedResults = data.backtests
        .map(result => {
          const processed = {
            ...result,
            id: result.id,
            startDate: result.startDate,
            endDate: result.endDate,
            cagrAnnualReturn: parseNumericValue(result.cagrAnnualReturn),
            maxDrawdown: parseNumericValue(result.maxDrawdown),
            totalReturn: parseNumericValue(result.totalReturn),
            sharpe: parseNumericValue(result.sharpe),
            sortino: parseNumericValue(result.sortino),
            romad: parseNumericValue(result.romad),
            createdAt: result.createdAt,
            updatedAt: result.updatedAt
          };
          console.log("Processed backtest result:", processed);
          return processed;
        });
      
      console.log("Final processed results:", processedResults);
      setBacktestHistory(processedResults);
      
      // Keep this section and enhance it to scroll to the newest backtest
      if (processedResults.length > 0) {
        // Sort by creation date (newest first)
        const sortedByDate = [...processedResults].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        if (sortedByDate.length > 0) {
          const mostRecent = sortedByDate[0];
          console.log("Auto-selecting most recent backtest:", mostRecent.id);
          setExpandedCardId(mostRecent.id);
          
          // Add small delay to allow for DOM update
          const highlightHandle = setTimeout(() => {
            const expandedCard = document.getElementById('expanded-card');
            if (expandedCard) {
              expandedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
              expandedCard.classList.add('highlight-animation');
              const removeHighlightHandle = setTimeout(() => {
                expandedCard.classList.remove('highlight-animation');
              }, 2000);
              unrefTimer(removeHighlightHandle);
            }
          }, 100);
          unrefTimer(highlightHandle);
        }
      }
    } catch (err) {
      console.error("History fetch error:", err);
      console.error("Error stack:", err.stack);
      
      // Parse error message to determine error type
      const errorMessage = err.message || "Unknown error";
      let userFriendlyMessage = "Error fetching backtest history";
      let shouldRetry = false;

      // Detect HTML error responses (common backend routing issues)
      if (errorMessage.includes("<!DOCTYPE html>") || 
          errorMessage.includes("Cannot GET /backtests/") ||
          errorMessage.includes("Invalid JSON response")) {
        userFriendlyMessage = "Backtest service is temporarily unavailable. This may be a server configuration issue.";
        shouldRetry = false; // Don't retry HTML errors - they indicate routing issues
      } 
      // Network connectivity issues
      else if (errorMessage.includes("Failed to fetch") || 
               errorMessage.includes("TypeError") ||
               errorMessage.includes("ECONNREFUSED") ||
               errorMessage.includes("Network error")) {
        userFriendlyMessage = "Network connection issue. Please check your internet connection.";
        shouldRetry = retryCount < maxRetries;
      }
      // HTTP error codes
      else if (errorMessage.includes("HTTP 404") || errorMessage.includes("404")) {
        userFriendlyMessage = "Strategy not found or has no backtest history yet.";
        shouldRetry = false; // Don't retry 404s
      }
      else if (errorMessage.includes("HTTP 403") || errorMessage.includes("403")) {
        userFriendlyMessage = "You don't have permission to view this strategy's backtest history.";
        shouldRetry = false; // Don't retry permission errors
      }
      else if (errorMessage.includes("HTTP 500") || errorMessage.includes("500")) {
        userFriendlyMessage = "Server error while fetching backtest history. Please try again.";
        shouldRetry = retryCount < maxRetries;
      }
      // Generic server errors that might be worth retrying
      else if (errorMessage.includes("timeout") || 
               errorMessage.includes("temporarily") ||
               retryCount === 0) {
        userFriendlyMessage = "Temporary error fetching backtest history. Retrying...";
        shouldRetry = retryCount < maxRetries;
      }
      else {
        // Last resort - show specific error but make it user-friendly
        userFriendlyMessage = `Error fetching backtest history: ${errorMessage}`;
      }

      // Attempt retry with exponential backoff
      if (shouldRetry) {
        console.log(`Retrying backtest history fetch (attempt ${retryCount + 1}/${maxRetries}) in ${retryDelay}ms`);
        setHistoryError(`${userFriendlyMessage} (Retry ${retryCount + 1}/${maxRetries})`);
        
        const retryHandle = setTimeout(() => {
          fetchBacktestHistory(retryCount + 1);
        }, retryDelay);
        unrefTimer(retryHandle);
        return;
      }

      // Final error - no more retries
      setHistoryError(userFriendlyMessage);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [baseStrategyId, getApiToken]); // Changed dependency to baseStrategyId

  /** pollBacktestWithId update to include more logging **/
  const pollBacktestWithId = useCallback(async (token, backtestId) => {
    if (!backtestId) {
      console.error("pollBacktestWithId called with no backtest ID!");
      return;
    }

    try {
      const data = await fetchWithAuth(token, `/backtests/${backtestId}/status`);
      console.log("Poll response:", data);

      // Reset network error counter on successful response
      networkErrorCountRef.current = 0;

      // Clear any connection errors since we got a successful response
      setError((prevError) => {
        if (prevError === "connection_issue") {
          console.log("Connection restored, clearing connection error");
          setIsLoading(true); // Resume loading state since backtest is still running
          return ""; // Clear the error
        }
        return prevError; // Keep other errors
      });

      const isRunning = data.running || false;
      const currentStage = cleanStatusKey(data.stage);
      const currentStatus = cleanStatusKey(data.status);
      const resolvedStatusKey = cleanStatusKey(currentStage || currentStatus);
      const statusDescription = data.status_description || data.statusDescription || "";
      
      // Extract progress info if available
      const progressInfo = data.backtest_progress?.[0] || null;

      // Update status display with progress
      const displayStatus = normalizeBacktestStatus(data.status, data.stage, isRunning);
      // If we're recovering from a connection issue, make sure to restore normal status
      setBacktestStatus((prevStatus) => {
        const wasConnectionError = prevStatus?.isConnectionError;
        if (wasConnectionError) {
          console.log("Restoring normal backtest status after connection recovery");
        }
        return {
          status: displayStatus,
          statusCode: resolvedStatusKey,
          progress: progressInfo,
          description: statusDescription || prevStatus?.description,
          isConnectionError: false, // Explicitly clear connection error flag
          running: isRunning // Track if backtest is actively running
        };
      });
      
      // Sync with server's elapsed time but keep the local timer running
      if (data.elapsed_ms) {
        const serverElapsedSeconds = Math.floor(data.elapsed_ms / 1000);
        elapsedTimeRef.current = serverElapsedSeconds;
        setElapsedTime(serverElapsedSeconds);
      }

      // Collect chart data
      if (progressInfo && progressInfo.percent !== undefined && progressInfo.portfolio_value) {
        const portfolioValue = parseFloat(progressInfo.portfolio_value.replace(/[^0-9.-]/g, ''));
        if (!isNaN(portfolioValue)) {
          // Parse positions for storage with data point
          let parsedPositions = null;
          try {
            if (progressInfo.positions_json) {
              const rawPositions = JSON.parse(progressInfo.positions_json);
              // Normalize position format and get top 5
              parsedPositions = rawPositions
                .map(pos => ({
                  symbol: pos.symbol || pos.asset?.symbol || 'Unknown',
                  quantity: pos.quantity ?? pos.qty ?? 0,
                  value: pos.value ?? pos.val ?? 0
                }))
                .filter(pos => pos.quantity > 0)
                .sort((a, b) => (b.value || 0) - (a.value || 0))
                .slice(0, 5);
            }
          } catch (e) {
            // Ignore JSON parse errors
          }

          // Parse cash value for display when no positions held
          let cashValue = null;
          if (progressInfo.cash) {
            cashValue = parseFloat(progressInfo.cash.replace(/[^0-9.-]/g, ''));
            if (isNaN(cashValue)) cashValue = null;
          }

          // Add to storage (including simulation_date for x-axis display, positions, and cash for tooltip)
          chartStorageRef.current.addDataPoint(
            backtestId,
            progressInfo.percent,
            portfolioValue,
            progressInfo.simulation_date,
            parsedPositions,
            cashValue
          );

          // Update local state for current display
          const chartFromStorage = chartStorageRef.current.getChart(backtestId);
          if (chartFromStorage && chartFromStorage.data) {
            setChartData(chartFromStorage.data);
          }
        }
      }
      
      // Define terminal states
      const isTerminalStateMatch = isTerminalStatus(resolvedStatusKey);

      // FIX: Continue polling for ANY non-terminal state, not just when isRunning or queued
      // Active statuses like "in_progress", "backtesting", "building", "deploying" should keep polling
      // even if isRunning is temporarily false (can happen during server communication gaps)
      const ACTIVE_STATUSES = ['in_progress', 'backtesting', 'building', 'deploying', 'running', 'queued', 'initiated'];
      const isActiveStatus = ACTIVE_STATUSES.includes(resolvedStatusKey);

      if (!isTerminalStateMatch && (isRunning || isActiveStatus)) {
        setIsLoading(true);
        return true; // Continue polling
      } else if (!isTerminalStateMatch) {
        // Unknown non-terminal status - keep polling but log for debugging
        console.log(`Unknown status "${resolvedStatusKey}" - continuing to poll`);
        setIsLoading(true);
        return true;
      } else {
        console.log(`Backtest ${backtestId} reached terminal state: ${resolvedStatusKey || currentStatus}`);
        
        if (isCompletedStatus(resolvedStatusKey)) {
          const isNoTrades = resolvedStatusKey === "completed_no_trades";
          await finalizeCompletedBacktest({
            statusKey: resolvedStatusKey,
            displayStatus,
            progressInfo,
            description: statusDescription,
            isNoTrades,
            stopPolling,
            stopTimer,
            setIsLoading,
            fetchBacktestHistory,
            setTableRefreshKey,
            setBacktestStatus,
            setError,
            onAnalytics: (payload) =>
              emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_COMPLETED_SUCCESS, {
                backtestId,
                durationSeconds: elapsedTimeRef.current,
                totalReturn: data?.raw_status?.total_return ?? null,
                sharpe: data?.raw_status?.sharpe ?? null,
                maxDrawdown: data?.raw_status?.max_drawdown ?? null,
                ...payload
              })
          });
          
          return false; // Stop polling
        } else if (resolvedStatusKey === "failed") {
          stopPolling();
          stopTimer();
          setIsLoading(false);
          const errorCode = data.error_details || data.raw_status?.error_code || data.status;
          const userFriendlyError = interpretErrorCode(errorCode);
          setError(userFriendlyError); // Ensure setError gets the interpreted message
          setErrorLogsExpanded(false);
          
          // --- Analytics: BACKTEST_FAILED (execution error) ---
          emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_FAILED, {
            backtestId,
            status: "failed",
            errorType: "execution_error",
            errorMessage: userFriendlyError,
            errorCode,
            failureStage: resolvedStatusKey || currentStatus
          });
          
          return false;
        }
        
        // For any other terminal state
        stopPolling();
        stopTimer();
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      console.error(`Error polling status for backtest ${backtestId}:`, err);
      if (err.message?.includes("not found")) {
        stopPolling();
        stopTimer();
        setIsLoading(false);
        setError(interpretErrorCode("ERROR_CODE_BACKTEST_NOT_FOUND")); // Use a specific or generic interpreted error
        
        // --- Analytics: BACKTEST_FAILED (not found) ---
        emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_FAILED, {
          backtestId,
          status: "failed",
          errorType: "not_found",
          errorMessage: "Backtest instance not found during polling.",
          failureStage: "polling"
        });
      } else {
        // For other unexpected polling errors (like network issues)
        // Don't immediately mark as failed - network errors are often temporary
        const isNetworkError = err.message?.includes("Failed to fetch") ||
                              err.message?.includes("Network error") ||
                              err.message?.includes("ERR_CONNECTION_REFUSED");

        if (isNetworkError) {
          networkErrorCountRef.current++;
          const maxNetworkRetries = 10; // Allow up to 10 consecutive network errors (20 seconds)

          if (networkErrorCountRef.current >= maxNetworkRetries) {
            // Too many consecutive network errors - show connection issue but keep polling
            console.error(`Too many network errors (${networkErrorCountRef.current}) while polling backtest ${backtestId}`);
            // Mark this as a connection issue, not a backtest failure
            setBacktestStatus({
              status: "Connection Issue",
              isConnectionError: true
            });
            setError("connection_issue"); // Special marker for connection issues
            // Don't stop polling or timer - connection might come back
            // Don't set isLoading to false - keep it true
            return true; // Continue polling to detect when connection returns
          } else {
            // For network errors, show a temporary warning but keep polling
            console.warn(`Network error ${networkErrorCountRef.current}/${maxNetworkRetries} while polling backtest ${backtestId}, will retry...`);
            // Don't set a permanent error, just return true to keep polling
            // The UI should remain in loading state
            return true; // Continue polling despite network error
          }
        } else {
          // For non-network errors, treat as failure
          setError(interpretErrorCode(err.message || "ERROR_CODE_POLLING_FAILED"));

          // --- Analytics: BACKTEST_FAILED (polling error) ---
          emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_FAILED, {
            backtestId,
            status: "failed",
            errorType: "polling_error",
            errorMessage: err.message,
            failureStage: "polling"
          });
        }
      }
      throw err; // Re-throw if necessary, or handle more gracefully
    }
  }, [
    stopPolling,
    stopTimer,
    fetchBacktestHistory,
    setBacktestStatus,
    setError,
    setElapsedTime,
    setIsLoading,
    setChartData,
    normalizeBacktestStatus,
    setErrorLogsExpanded
  ]);

  // Simple reference to track if we've attempted recovery
  const recoveryRef = useRef(false);
  // Track consecutive network errors to avoid infinite retries
  const networkErrorCountRef = useRef(0);
  
  // Add window-level tracking to prevent duplicate polling and track active backtest
  const pollingStatusRef = useRef({
    isPolling: false,
    backtestId: null,
    pollCount: 0
  });

  // Define startPollingWithExplicitId after pollBacktestWithId
  const startPollingWithExplicitId = useCallback((backtestId) => {
    if (!backtestId) {
      console.warn("Attempted to start polling with no backtest ID");
      return;
    }
    
    // Clean up any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    console.log(`Starting polling with explicit backtest ID: ${backtestId}`);
    
    // Update polling reference state
    pollingStatusRef.current = {
      isPolling: true,
      backtestId: backtestId,
      pollCount: 0
    };
    
    // Do an immediate poll
    (async () => {
      try {
        const token = await getApiToken();
        // --- Call pollBacktestWithId directly ---
        await pollBacktestWithId(token, backtestId);
      } catch (err) {
        console.error(`Initial poll error for ID ${backtestId}:`, err);
      }
    })();
    
    // Set up interval polling every 2 seconds
    pollingRef.current = setInterval(async () => {
      // --- Check polling status ref ---
      if (!pollingStatusRef.current.isPolling || pollingStatusRef.current.backtestId !== backtestId) {
        console.log("Polling stopped or backtest ID changed, clearing interval");
        clearInterval(pollingRef.current);
        pollingRef.current = null; // Clear the ref
        return;
      }

      try {
        const token = await getApiToken();
        // --- FIX: Call pollBacktestWithId instead of startPollingWithExplicitId ---
        // --- Pass the correct backtestId from the closure ---
        const continuePolling = await pollBacktestWithId(token, backtestId);
        // --- Stop interval if pollBacktestWithId returns false ---
        if (!continuePolling) {
           pollingStatusRef.current.isPolling = false; // Update status ref
           clearInterval(pollingRef.current);
           pollingRef.current = null; // Clear the ref
           console.log(`Polling stopped by pollBacktestWithId for ${backtestId}`);
        }
        // --- End Fix ---
      } catch (err) {
        console.error(`Polling error for ID ${backtestId}:`, err);
        if (err.message?.includes("not found")) {
          stopPolling(); // stopPolling clears the interval via pollingRef.current
          stopTimer();
          setIsLoading(false);
          pollingStatusRef.current.isPolling = false; // Update status ref
        }
        // Optionally stop polling on other errors too? Depends on desired behavior.
        // For now, only stop on "not found".
      }
    }, 2000); // Poll every 2 seconds
    unrefTimer(pollingRef.current);
    
    // Ensure polling gets cleaned up on component unmount or when explicitly stopped
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      // Reset polling status ref on cleanup
      pollingStatusRef.current = {
        isPolling: false,
        backtestId: null,
        pollCount: 0
      };
    };
  }, [getApiToken, stopPolling, stopTimer, setIsLoading, pollBacktestWithId]); // <-- FIXED: use pollBacktestWithId, not pollBacktestWithExplicitId

  /** Define pollBacktestStatus before startPolling which depends on it */
  const pollBacktestStatus = useCallback(async () => {
    // Log the current ID to help debug
    console.log(`Polling with currentBacktestId: ${currentBacktestId}`);
    
    try {
      // Get access token first
      const token = await getApiToken();
      
      // If we don't have a backtest ID, we need to find it
      if (!currentBacktestId) {
        console.log("No backtest ID available, stopping polling");
        stopPolling();
        stopTimer();
        setIsLoading(false);
        return;
      }
      
      // If we have a backtest ID, poll it directly
      await pollBacktestWithId(token, currentBacktestId);
      
    } catch (err) {
      console.error("Poll status error:", err);
      // Only show error if we have a backtest ID
      if (currentBacktestId) {
        setError("Lost connection to server.");
      }
      stopPolling();
      stopTimer();
      setIsLoading(false);
    }
  }, [
    currentBacktestId, 
    getApiToken, 
    stopPolling, 
    stopTimer, 
    setError,
    setIsLoading,
    pollBacktestWithId
  ]);

  /** detectInProgressBacktest with improved loading state management */
  const detectInProgressBacktest = useCallback(async (retryCount = 0) => {
    // --- Wait for baseStrategyId ---
    if (!baseStrategyId) {
      console.log("[detectInProgressBacktest] Waiting for baseStrategyId...");
      return false;
    }
    // ---

    const maxRetries = 2; // Fewer retries for this function since it's called multiple times
    const retryDelay = Math.min(2000 * Math.pow(2, retryCount), 10000); // Exponential backoff

    try {
      console.log("Detecting in-progress backtests for baseStrategyId:", baseStrategyId); // Use baseStrategyId

      const token = await getApiToken();
      // Use baseStrategyId for the stats endpoint
      const data = await fetchWithAuth(token, `/backtests/${baseStrategyId}/stats`);
      
      // First update backtest history
      if (data.backtests) {
        setBacktestHistory(data.backtests);
      }

      // Get the most recent backtest only
      if (!data.backtests?.length) return false;
      
      const mostRecent = [...data.backtests].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];

      console.log("Most recent backtest:", mostRecent);
      
      // Define active statuses to check
      const activeStatuses = [
        "in_progress", "initiated", "queued", "building", 
        "deploying", "running", "backtesting"
      ];
      
      // Check if most recent backtest is active
      const status = (mostRecent.status || '').toLowerCase();
      const stage = (mostRecent.stage || '').toLowerCase();
      const isActive = activeStatuses.includes(status) || activeStatuses.includes(stage);
      
      if (isActive) {
        // Set loading state immediately
        setIsLoading(true);
        
        // Verify backtest is still active via status endpoint
        const statusResponse = await fetchWithAuth(
          token,
          `/backtests/${mostRecent.id}/status`
        );
        
        console.log("Status response for most recent backtest:", statusResponse);
        
        const isRunning = statusResponse.running || false;
        const currentStage = (statusResponse.stage || '').toLowerCase();
        const currentStatus = (statusResponse.status || '').toLowerCase();
        
        if (isRunning || activeStatuses.includes(currentStage) || activeStatuses.includes(currentStatus)) {
          // Update UI state
          setStartDate(mostRecent.startDate);
          setEndDate(mostRecent.endDate);
          setCurrentBacktestId(mostRecent.id);
          setBacktestStatus(normalizeBacktestStatus(statusResponse.status, statusResponse.stage, isRunning));

          // Load existing chart data from storage
          const existingChart = chartStorageRef.current.getChart(mostRecent.id);
          if (existingChart && existingChart.data) {
            setChartData(existingChart.data);
          }

          // Calculate elapsed time
          const elapsedSeconds = Math.floor((new Date() - new Date(mostRecent.createdAt)) / 1000);
          startTimer(elapsedSeconds);
          
          // Start polling
          console.log("Starting polling for active backtest:", mostRecent.id);
          startPollingWithExplicitId(mostRecent.id); // Correct function call

          return true;
        }
      }
      
      console.log("No active backtests found");
      setIsLoading(false);
      setInitialLoadComplete(true);
      return false;
    } catch (err) {
      console.error("Error detecting in-progress backtests:", err);
      
      const errorMessage = err.message || "Unknown error";

      // Handle specific error types - CLEAR MESSAGING for 404s
      if (errorMessage.includes("<!DOCTYPE html>") || 
          errorMessage.includes("Cannot GET /backtests/") ||
          errorMessage.includes("Invalid JSON response")) {
        console.error("🚨 BACKEND ISSUE: Backtest router not mounted in Express app!");
        console.error("The /backtests/:strategy_id/stats endpoint returns 404 HTML page");
        console.error("This needs to be fixed in the backend - the backtest router is not registered");
        setIsLoading(false);
        setInitialLoadComplete(true);
        return false;
      } 
      else if (errorMessage.includes("HTTP 404") || errorMessage.includes("404")) {
        console.error("🚨 BACKEND CONFIGURATION: /backtests/:strategy_id/stats endpoint not found");
        setIsLoading(false);
        setInitialLoadComplete(true);
        return false;
      }
      else if (errorMessage.includes("Failed to fetch") || 
               errorMessage.includes("Network error") ||
               errorMessage.includes("timeout")) {
        console.warn("Network error detecting in-progress backtests");
        // Only retry for genuine network issues
        const shouldRetry = retryCount < maxRetries;
        if (shouldRetry) {
          console.log(`Retrying in-progress backtest detection (attempt ${retryCount + 1}/${maxRetries}) in ${retryDelay}ms`);
          const retryHandle = setTimeout(() => {
            detectInProgressBacktest(retryCount + 1);
          }, retryDelay);
          unrefTimer(retryHandle);
          return false;
        }
      }
      else {
        console.warn("Unexpected error detecting in-progress backtests:", errorMessage);
      }

      // Final fallback - no more retries
      setIsLoading(false);
      setInitialLoadComplete(true);
      return false;
    }
  }, [
    baseStrategyId,
    getApiToken,
    setCurrentBacktestId,
    setStartDate,
    setEndDate,
    startTimer,
    setIsLoading,
    setBacktestStatus,
    setChartData,
    setBacktestHistory,
    normalizeBacktestStatus,
    startPollingWithExplicitId // Ensure this dependency is correct
  ]);

  /** Initialize backtest state if needed - with improved safety */
  const initializeBacktestState = useCallback(async () => {
    if (!aiStrategyId || recoveryAttempted || recoveryRef.current) return;

    try {
      // Mark that we're attempting recovery
      recoveryRef.current = true;
      setRecoveryAttempted(true);
      setError("");
      
      // Reset polling state
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      pollingStatusRef.current = {
        isPolling: false,
        backtestId: null,
        pollCount: 0
      };
      
      // Check for any in-progress backtest
      const recoveredBacktest = await detectInProgressBacktest();
      if (recoveredBacktest) {
        console.log("Successfully recovered an in-progress backtest");
        setIsLoading(true);
        return;
      }
      
      console.log("No in-progress backtest found");
      setIsLoading(false);
      setInitialLoadComplete(true);
    } catch (err) {
      console.error("Init error:", err);
      if (currentBacktestId) {
        setError(interpretErrorCode("ERROR_CODE_CONNECTION_FAILED")); // Interpret generic message
      }
      setIsLoading(false);
      stopPolling();
      stopTimer();
      setRecoveryAttempted(true);
    }
  }, [
    aiStrategyId,
    recoveryAttempted,
    detectInProgressBacktest,
    currentBacktestId,
    setIsLoading,
    setError,
    stopPolling,
    stopTimer
  ]);

  // Fetch strategy code using aiStrategyId and revisionId
  const fetchStrategyCode = useCallback(async () => {
    // --- Wait for baseStrategyId (needed for strategy name) and aiStrategyId/revisionId ---
    if (!aiStrategyId || !revisionId || !baseStrategyId) {
       console.log("[fetchStrategyCode] Waiting for required IDs:", { aiStrategyId, revisionId, baseStrategyId });
       return;
    }
    // ---

    try {
      const token = await getApiToken();
      const strategyDetails = await fetchWithAuth(token, `/strategies/${baseStrategyId}`);
      if (strategyDetails?.name) {
        setStrategyName(strategyDetails.name);
      }
      setIsStrategyPublic(Boolean(strategyDetails?.isPublic));

      console.log("Fetching versions for aiStrategyId:", aiStrategyId);
      const versionsData = await fetchWithAuth(token, `/ai-bot-builder/list-versions?aiStrategyId=${aiStrategyId}`);
      console.log("Versions data received:", versionsData?.versions?.length, "versions");
      
      if (!versionsData?.versions?.length) {
        throw new Error("No versions found for this strategy");
      }

      console.log("Looking for revisionId:", revisionId);
      const matchingVersion = versionsData.versions.find(v => v.revisionId === revisionId);
      console.log("Matching version found:", matchingVersion ? "Yes" : "No");
      
      if (!matchingVersion) {
        throw new Error(`No matching version found for revision ID: ${revisionId}`);
      }
      
      const requirements = Array.isArray(matchingVersion.dataRequirements)
        ? matchingVersion.dataRequirements
        : [];
      setStrategyDataRequirements(requirements);
      const assetClasses = Array.isArray(matchingVersion.assetClasses)
        ? dedupeOrderedTokens(matchingVersion.assetClasses)
        : [];
      setStrategyAssetClasses(assetClasses);

      const code = matchingVersion.code_out || ""; // Use code_out or default to empty string
      if (!code) {
        console.warn("No code found for this revision, setting empty string.");
        // throw new Error("No code found for this revision"); // Optionally throw error
      }

      console.log(`Strategy code loaded. Length: ${code.length} characters`);
      console.log("First 100 chars:", code.substring(0, 100));

      setStrategyCode(code); // Update state with fetched code
      parseStrategyDetails(code);
    } catch (err) {
      console.error("[fetchStrategyCode] Error:", err);
      setError(interpretErrorCode(err.message || "ERROR_CODE_FETCH_STRATEGY_CODE"));
      setStrategyCode(""); // Clear code on error
    }
  }, [aiStrategyId, revisionId, baseStrategyId, getApiToken, parseStrategyDetails]);

  const handleShareStrategy = useCallback(async (_unusedPassword, shareType = 'public') => {
    if (isSharingStrategy) {
      return false;
    }

    if (!baseStrategyId || !aiStrategyId) {
      alert('Unable to determine strategy identifiers. Please try reloading the page.');
      return false;
    }

    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      console.error('[BacktestPage] Missing REACT_APP_API_URL environment variable');
      alert('Sharing is unavailable right now. Please contact support.');
      return false;
    }

    setIsSharingStrategy(true);

    try {
      const token = await getApiToken();
      if (!token) {
        throw new Error('Missing authentication token');
      }

      if (shareType === 'private') {
        const response = await fetch(`${apiBase}/strategies/${baseStrategyId}/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            settings: {
              allowCodeView: true,
              allowClone: true,
              requirePassword: true
            }
          })
        });

        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.shareToken) {
          throw new Error(result?.message || 'Failed to create protected link');
        }

        const fullShareUrl = `${window.location.origin}/ai-bot-builder/${aiStrategyId}?share=${result.shareToken}`;
        console.log('[BacktestPage] Private share link created:', fullShareUrl);

        return {
          success: true,
          isPrivate: true,
          shareUrl: fullShareUrl,
          shareToken: result.shareToken
        };
      }

      const response = await fetch(`${apiBase}/strategies/${baseStrategyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isPublic: true })
      });

      const updated = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(updated?.message || 'Failed to make strategy public');
      }

      setIsStrategyPublic(Boolean(updated?.isPublic));
      setShowShareModal(false);
      console.log('[BacktestPage] Strategy marked public from backtest view.');
      return true;
    } catch (error) {
      console.error('[BacktestPage] Error sharing strategy:', error);
      alert(error.message || 'Failed to share strategy. Please try again.');
      return false;
    } finally {
      setIsSharingStrategy(false);
    }
  }, [aiStrategyId, baseStrategyId, getApiToken, isSharingStrategy]);


  // --- UPDATED: Validate backtest parameters ---
  const validateBacktestParams = useCallback(() => {
    if (!strategyCode || strategyCode.trim() === '') {
      return 'Strategy code is missing. Please refresh the page or check the AI Bot Builder.';
    }
    if (!startDate || !endDate) {
      return 'Please select valid start and end dates';
    }
    if (new Date(startDate) >= new Date(endDate)) {
      return 'Start date must be before end date';
    }
    // --- NEW: Check for missing required env vars ---
    if (requiredVarsStatus.missing.length > 0) {
      return `Missing required environment variable(s): ${requiredVarsStatus.missing.join(', ')}. Please provide them in the Environment Variables section.`;
    }

    return null; // No errors
  }, [strategyCode, startDate, endDate, requiredVarsStatus]); // Depend on requiredVarsStatus

  const ensureThetaAccessBeforeRun = useCallback(async () => {
    if (!shouldUseTheta) {
      return { ok: true };
    }

    console.log('[BacktestPage] ensureThetaAccessBeforeRun invoked. Skipping client-side entitlement gate and relying on backend.', {
      selectedProvider,
      purchasedProductsParam,
      cartSummary: {
        items: cart?.items?.length || 0,
        pending: cartPending,
        mutationError: cartMutationError || null,
      },
    });

    // Try to refresh Theta access summary purely for debugging/analytics, but never block the run.
    if (!thetaAccessSummary && !latestThetaSummaryRef.current) {
      try {
        setThetaAccessLoading(true);
        const token = await getApiToken();
        const summary = await fetchWithAuth(token, `/data-providers/access?provider=${THETA_PROVIDER_SLUG}`);
        setThetaAccessSummary(summary);
        latestThetaSummaryRef.current = summary;
      } catch (err) {
        console.warn('[BacktestPage] Non-blocking Theta access refresh failed; continuing anyway:', err);
      } finally {
        setThetaAccessLoading(false);
      }
    }

    return { ok: true };
  }, [
    shouldUseTheta,
    cart,
    cartPending,
    cartMutationError,
    selectedProvider,
    purchasedProductsParam,
    thetaAccessSummary,
    getApiToken,
  ]);

  const handleStartThetaTrial = useCallback(async () => {
    if (selectedProvider !== 'theta_data' && selectedProvider !== 'botspot_auto') {
      setShowTrialModal(false);
      return;
    }
    if (!pendingTrialProducts.length) {
      setShowTrialModal(false);
      return;
    }

    try {
      setTrialRequestPending(true);
      setTrialModalError(null);
      const token = await getApiToken();
      const productSlugs = pendingTrialProducts.map((product) => product.productSlug);
      const summary = await fetchWithAuth(token, `/data-providers/access/start-trial`, {
        method: "POST",
        body: { productSlugs },
      });
      setThetaAccessSummary(summary);
      latestThetaSummaryRef.current = summary;

      const refreshedProducts = deriveThetaProducts(summary);
      const stillMissing = refreshedProducts.filter((product) => !productHasActiveAccess(product));

      if (stillMissing.length > 0) {
        // Trials exhausted for at least one required SKU (e.g., stocks already cancelled)
        queueProductSelections(stillMissing);
        setPendingTrialProducts(stillMissing);
        setShowTrialModal(false);
        setShowPlansModal(true);
        setCheckoutError('Your free trial is no longer available for all required Theta Data products. Please purchase a plan to continue.');
        setThetaAccessError(null);
        emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_TRIAL_STARTED, {
          providerSlug: THETA_PROVIDER_SLUG,
          productSlugs,
          outcome: 'trial_unavailable',
        });
        return;
      }

      setPendingTrialProducts([]);
      setShowTrialModal(false);
      setThetaAccessError(null);
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_TRIAL_STARTED, {
        providerSlug: THETA_PROVIDER_SLUG,
        productSlugs,
        outcome: 'trial_started',
      });
      const resumeHandle = setTimeout(() => {
        runBacktestRef.current?.();
      }, 0);
      unrefTimer(resumeHandle);
    } catch (err) {
      console.error('[BacktestPage] Failed to start Theta trial:', err);
      setTrialModalError(err?.message || 'Failed to start trial. Please try again.');
    } finally {
      setTrialRequestPending(false);
    }
  }, [pendingTrialProducts, getApiToken, selectedProvider, deriveThetaProducts, queueProductSelections]);

  const handleCloseTrialModal = useCallback(() => {
    if (trialRequestPending) return;
    setShowTrialModal(false);
    setTrialModalError(null);
  }, [trialRequestPending]);

  const handleCloseGuardrailModal = useCallback(() => {
    setShowGuardrailModal(false);
  }, []);

  const handleAdjustDatesAndRun = useCallback(() => {
    const today = clampToMiddayUTC(new Date());
    if (!today) {
      setShowGuardrailModal(false);
      return;
    }

    const earliestStart = clampToMiddayUTC(new Date(today));
    earliestStart.setUTCDate(earliestStart.getUTCDate() - THETA_TRIAL_LOOKBACK_DAYS);

    const existingStart = clampToMiddayUTC(startDate || earliestStart);
    let nextStart = existingStart && existingStart > earliestStart ? existingStart : earliestStart;

    const existingEnd = clampToMiddayUTC(endDate || new Date(nextStart));
    const minEnd = clampToMiddayUTC(new Date(nextStart));
    minEnd.setUTCDate(minEnd.getUTCDate() + GUARDRAIL_MIN_RANGE_DAYS);

    let nextEnd = existingEnd && existingEnd > minEnd ? existingEnd : minEnd;

    setStartDate(nextStart);
    setEndDate(nextEnd);
    setGuardrailDetails(null);
    setShowGuardrailModal(false);

    const resumeHandle = setTimeout(() => {
      runBacktestRef.current?.();
    }, 0);
    unrefTimer(resumeHandle);
  }, [startDate, endDate]);

  const handleOpenPlansFromGuardrail = useCallback(() => {
    setShowGuardrailModal(false);
    setCheckoutError(null);
    setShowPlansModal(true);
  }, []);

  const handleClosePlansModal = useCallback(() => {
    setShowPlansModal(false);
    setCheckoutError(null);
  }, []);

  // --- handleRunBacktest remains largely the same, but validation is updated ---
  async function handleRunBacktest() {
    console.log('[BacktestPage] Run backtest requested', {
      provider: selectedProvider,
      shouldUseTheta,
      purchasedProductsParam,
      cartItems: cart?.items?.length || 0,
      cartPending,
      thetaAccessLoading,
    });

    // --- Use updated validation ---
    const validationError = validateBacktestParams();
    if (validationError) {
      setError(validationError); // validationError is already a user-friendly string
      // Scroll to the environment section if the error is related to missing vars
      if (validationError.includes("Missing required environment variable")) {
         const envVarElement = document.querySelector('.saved-env-var-set-selector');
         envVarElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    if (isRunDisabled) return; // Double check just in case

    const accessCheck = await ensureThetaAccessBeforeRun();
    if (!accessCheck.ok) {
      return;
    }

    // 🎉 CELEBRATE THE ACTION IMMEDIATELY! 🎉
    createEnhancedSparkleEffect();

    // Reset network error counter when starting a new backtest
    networkErrorCountRef.current = 0;

    // Clear all previous state
    setError("");
    setBacktestStatus(null);
    setCurrentBacktestId(null);
    setChartData([]); // Clear chart data when starting new backtest
    stopTimer();
    stopPolling();
    setElapsedTime(0);

    setIsLoading(true);
    setShowStartAnimation(true);
    setBacktestStatus("Initiating");

    try {
      const token = await getApiToken();

      console.log("Strategy code length:", strategyCode?.length || 0);
      console.log("First 100 chars of code:", strategyCode?.substring(0, 100) || "NO CODE");

      const backtestParams = buildBacktestStartParams({
        aiStrategyId,
        strategyCode,
        startDate,
        endDate,
        revisionId,
        selectedProvider,
        savedEnvVarSetIds,
        linkedStrategyId,
      });

      // SECURITY: Mask sensitive values in logs to prevent API key exposure
      console.log("Starting backtest with params:", {
        ...backtestParams,
        main: backtestParams.main ? `[Code length: ${backtestParams.main.length} chars]` : "[MISSING]",
      });

      const response = await startBacktest(token, backtestParams);

      if (response.backtestId) {
        console.log(`Backtest started with ID: ${response.backtestId}`);
        setCurrentBacktestId(response.backtestId);

        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        pollingStatusRef.current = { isPolling: false, backtestId: null, pollCount: 0 };

        startPollingWithExplicitId(response.backtestId);
        startTimer(0);
        
        // --- Analytics: BACKTEST_STARTED ---
        emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_STARTED, {
          backtestId: response.backtestId,
          managerBotId: response.manager_bot_id || null,
          startDate,
          endDate,
          envVarsCount: selectedSavedEnvVarKeyNames.length,
          status: "in_progress"
        });
      } else {
        // This case should ideally be caught by startBacktest and return a structured error
        throw new Error("ERROR_CODE_NO_BACKTEST_ID"); 
      }
    } catch (err) {
      console.error("RunBacktest error:", err);

      // Extract errorCode from err.data.errorCode (the correct field name from backend)
      // Note: err.data.error is a boolean (true), err.data.errorCode is the string code
      const errorCode = err?.data?.errorCode || err?.data?.error || err?.response?.error;
      const errorDetails = err?.data?.details || err?.data; // Fallback to err.data for flat responses

      if (err?.status === 403 && errorCode === 'DATA_ACCESS_REQUIRED' && shouldUseTheta) {
        console.warn('[BacktestPage] Backend reported DATA_ACCESS_REQUIRED. Showing Theta trial modal.', {
          errorDetails,
        });
        const missingProducts = Array.isArray(errorDetails?.missingProducts) ? errorDetails.missingProducts : thetaRequiredProducts;
        setPendingTrialProducts(missingProducts);
        queueProductSelections(missingProducts);
        setShowTrialModal(true);
        stopTimer();
        stopPolling();
        setIsLoading(false);
        setBacktestStatus(null);
        setError('');
        return;
      }

      if (err?.status === 403 && errorCode === 'TRIAL_RANGE_EXCEEDED' && shouldUseTheta) {
        console.warn('[BacktestPage] Backend reported TRIAL_RANGE_EXCEEDED. Showing guardrail modal.', {
          errorDetails,
        });
        const limitDays = errorDetails?.lookbackLimitDays || THETA_TRIAL_LOOKBACK_DAYS;
        const fallbackToday = clampToMiddayUTC(new Date());
        const requestedStartClamped = clampToMiddayUTC(errorDetails?.requestedStartDate || startDate);
        const startDaysAgo = requestedStartClamped && fallbackToday
          ? calculateDaysBetween(requestedStartClamped, fallbackToday)
          : selectedStartDaysAgo;
        setGuardrailDetails({
          provider: errorDetails?.provider || thetaAccessSummary?.provider || null,
          products: errorDetails?.product ? [errorDetails.product] : thetaTrialRangeConflicts,
          requestedDays: errorDetails?.requestedLookbackDays || selectedLookbackDays,
          startDaysAgo,
          limitDays,
        });
        queueProductSelections(errorDetails?.product ? [errorDetails.product] : thetaTrialRangeConflicts);
        setShowGuardrailModal(true);
        stopTimer();
        stopPolling();
        setIsLoading(false);
        setBacktestStatus(null);
        setError('');
        return;
      }

      // Handle backtest limit reached (429 Too Many Requests)
      // Show PaywallModal instead of "Backtest Failed" error
      // Backend returns limitType: 'backtest' (count) or 'backtest_minutes' (runtime)
      if (err?.status === 429 && errorCode === 'BACKTEST_LIMIT_REACHED') {
        const backendLimitType = errorDetails?.limitType || 'backtest';
        console.log('[BacktestPage] Backtest limit reached. Showing PaywallModal.', {
          errorDetails,
          backendLimitType,
        });

        // Normalize usage shape: backtest_minutes uses usedMinutes/limitMinutes fields,
        // while PaywallModal expects used/limit for display
        const usage = backendLimitType === 'backtest_minutes'
          ? {
              used: errorDetails?.usage?.usedMinutes ?? 0,
              limit: errorDetails?.usage?.limitMinutes ?? 0,
              remaining: errorDetails?.usage?.remainingMinutes ?? 0,
              resetsAt: errorDetails?.usage?.resetsAt,
            }
          : errorDetails?.usage || { used: 0, limit: 0, remaining: 0 };

        setPaywallData({
          limitType: backendLimitType,
          usage,
          checkoutUrl: errorDetails?.checkoutUrl || '/checkout/starter-plan',
        });
        setShowPaywallModal(true);
        stopTimer();
        stopPolling();
        setIsLoading(false);
        setBacktestStatus(null);
        setError(''); // Don't show "Backtest Failed" - the modal is more appropriate
        return;
      }

      let errorMessageString = "ERROR_CODE_FAILED_TO_START"; // Default error code
      if (err.message) {
        // Attempt to parse if err.message is a JSON string
        if (typeof err.message === 'string' && err.message.startsWith('{') && err.message.endsWith('}')) {
           try {
              const parsed = JSON.parse(err.message);
              errorMessageString = parsed.error_details || parsed.error || parsed.message || err.message;
           } catch (parseErr) {
              errorMessageString = err.message; // Use original message if parsing fails
           }
        } else {
          errorMessageString = err.message; // Use original message if not JSON
        }
      }
      
      const finalUserFriendlyError = interpretErrorCode(errorMessageString);
      updateBacktestStatus("failed", finalUserFriendlyError); // Pass interpreted message
      setErrorLogsExpanded(true);
      
      // --- Analytics: BACKTEST_FAILED (startup error) ---
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_FAILED, {
        backtestId: currentBacktestId,
        status: "failed",
        errorType: "startup_error",
        errorMessage: err.message,
        failureStage: "start"
      });
    } finally {
      setIsLoading(false);
      setShowStartAnimation(false);
    }
  }
  runBacktestRef.current = handleRunBacktest;

  useEffect(() => {
    if (!autoRunReady) return;
    if (!runBacktestRef.current) return;
    runBacktestRef.current();
    setShouldAutoRun(false);
    setCheckoutStatus(null);
    setPurchasedProductsParam("");
  }, [autoRunReady]);

  // Add analytics for stopping a backtest
  const handleForceStop = async () => {
    setBacktestStatus("Stopping");
    try {
      const token = await getApiToken();
      
      const stopParams = currentBacktestId 
        ? { backtest_id: currentBacktestId }
        : {
            bot_id: aiStrategyId, // Use aiStrategyId
            revision_id: revisionId, // Use revisionId
            start_date: startDate,
            end_date: endDate
          };
      
      await stopBacktest(token, stopParams);
      
      setBacktestStatus("Stopped");
      setError("");
      setIsLoading(false);
      stopPolling();
      stopTimer();
      
      // --- Analytics: BACKTEST_STOPPED_BY_USER ---
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_STOPPED_BY_USER, {
        backtestId: currentBacktestId,
        status: "stopped"
      });
    } catch (err) {
      console.error("Stop error:", err);
      setError(interpretErrorCode(err.message || "ERROR_CODE_STOP_FAILED"));
      setIsLoading(false);
      
      // --- Analytics: BACKTEST_FAILED (stop error) ---
      emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_FAILED, {
        backtestId: currentBacktestId,
        status: "stop_failed",
        errorType: "stop_failed",
        errorMessage: err.message,
        failureStage: "stop"
      });
    }
  };

  // --- Analytics: Date Range Change Handlers ---
  const handleStartDateChange = useCallback((date) => {
    setStartDate(date);
    emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_DATE_RANGE_CHANGED, {
      newStartDate: date,
      newEndDate: endDate,
      rangeMode,
      backtestId: currentBacktestId
    });
  }, [aiStrategyId, revisionId, baseStrategyId, endDate, rangeMode, currentBacktestId, emitBacktestEvent]);

  const handleEndDateChange = useCallback((date) => {
    setEndDate(date);
    emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_DATE_RANGE_CHANGED, {
      newStartDate: startDate,
      newEndDate: date,
      rangeMode,
      backtestId: currentBacktestId
    });
  }, [aiStrategyId, revisionId, baseStrategyId, startDate, rangeMode, currentBacktestId, emitBacktestEvent]);

  // --- Handle "Fix Using AI" navigation ---
  const handleFixError = useCallback(() => {
    if (!aiStrategyId) return;
    const prompt = "The backtest failed with an error. Please fix the strategy.";
    navigateToAiAgent(navigate, {
      prompt,
      aiStrategyId,
      revisionId,
      source: "backtest-failed",
      context: {
        backtestId: currentBacktestId,
        status: backtestStatus?.status || backtestStatus?.statusCode,
        error: error || backtestStatus?.description || backtestStatus?.error_details,
      },
    }, { replace: false });
  }, [aiStrategyId, revisionId, currentBacktestId, backtestStatus?.status, backtestStatus?.statusCode, backtestStatus?.description, backtestStatus?.error_details, error, navigate]);

  const handleFixNoTrades = useCallback(() => {
    if (!aiStrategyId) return;
    const prompt = "The backtest completed without executing trades. Please adjust the strategy rules so that at least one position can open within the tested range.";
    navigateToAiAgent(navigate, {
      prompt,
      aiStrategyId,
      revisionId,
      source: "backtest-no-trades",
      context: {
        backtestId: currentBacktestId,
        status: backtestStatus?.status || backtestStatus?.statusCode,
        noTrades: true,
      },
    }, { replace: false });
  }, [aiStrategyId, revisionId, currentBacktestId, backtestStatus?.status, backtestStatus?.statusCode, navigate]);

  // --- Analytics: Error Logs Toggle Handler ---
  const handleErrorLogsToggle = useCallback(() => {
    setErrorLogsExpanded((expanded) => {
      const newExpanded = !expanded;
      if (newExpanded) {
        emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_LOGS_VIEWED, {
          backtestId: currentBacktestId,
          logType: "error"
        });
      }
      return newExpanded;
    });
  }, [aiStrategyId, revisionId, baseStrategyId, currentBacktestId, emitBacktestEvent]);

  // --- Analytics: Live Logs Toggle Handler ---
  const handleLiveLogsToggle = useCallback(() => {
    setLiveLogsExpanded((expanded) => {
      const newExpanded = !expanded;
      if (newExpanded && currentBacktestId) {
        emitBacktestEvent(ANALYTICS_EVENTS.BACKTEST_LOGS_VIEWED, {
          backtestId: currentBacktestId,
          logType: "live"
        });
      }
      return newExpanded;
    });
  }, [aiStrategyId, revisionId, baseStrategyId, currentBacktestId, emitBacktestEvent]);

  /** Top card render function **/
  function renderTopCard() {
    // --- NEW: Check if Perplexity is required ---
    const needsPerplexity = requiredVarsStatus.required.includes("PERPLEXITY_API_KEY");
    const providerDisplayName = selectedProviderInfo?.displayName || selectedProviderInfo?.name
      || (selectedProvider === "custom" ? "Custom Provider / BYO credentials" : selectedProvider);
    const formattedStartDate = startDate
      ? new Date(startDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
      : "Not set";
    const formattedEndDate = endDate
      ? new Date(endDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
      : "Not set";
    const renderDateRangeSection = (lockedView = false) => (
      <div className="date-range-selector">
        <CustomDatePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onRangeModeChange={setRangeMode}
          rangeMode={rangeMode}
          isLoading={lockedView ? false : isLoading}
          backtestStatus={backtestStatus}
          disabled={lockedView ? true : isLoading}
        />
      </div>
    );

    const renderProviderSection = (lockedView = false) => {
      const coverageWarning = providerCoverageWarningInfo;

      const helperMessages = [];
      if (selectedProvider === 'custom') {
        helperMessages.push(
          'Set your provider credentials in the Environment Variables section below before running the backtest.',
        );
      }
      if (lockedView) {
        helperMessages.push('Configuration locked while this backtest runs.');
      }

      // When locked, always show the provider selector without collapsible wrapper
      if (lockedView) {
        return (
          <ProviderSelector
            id="data-provider-select"
            label="Select your data source"
            providers={providerOptionsForRender}
            value={selectedProvider}
            onChange={(next) => {
              providerOverrideRef.current = true;
              setSelectedProvider(next);
            }}
            loading={providerCatalogLoading}
            error={providerCatalogError}
            helperText={helperMessages.length ? helperMessages.join(' ') : null}
            coverageWarning={coverageWarning}
            disabled={lockedView}
          />
        );
      }

      // When not locked, wrap in collapsible configuration section
      return (
        <div className={`optional-config-section ${providerSectionCollapsed ? 'collapsed' : ''}`}>
          <div
            className="optional-config-header"
            onClick={() => setProviderSectionCollapsed(!providerSectionCollapsed)}
          >
            <div className="optional-config-title-group">
              <span className="optional-config-title">Data Source</span>
              <span className="optional-config-subtitle">(optional)</span>
            </div>
            <button className="optional-config-toggle" type="button">
              <FontAwesomeIcon icon={providerSectionCollapsed ? faChevronDown : faChevronUp} />
            </button>
          </div>
          {!providerSectionCollapsed && (
            <div className="optional-config-content">
              <ProviderSelector
                id="data-provider-select"
                label="Select your data source"
                providers={providerOptionsForRender}
                value={selectedProvider}
                onChange={(next) => {
                  providerOverrideRef.current = true;
                  setSelectedProvider(next);
                }}
                loading={providerCatalogLoading}
                error={providerCatalogError}
                helperText={helperMessages.length ? helperMessages.join(' ') : null}
                coverageWarning={coverageWarning}
                disabled={false}
              />
            </div>
          )}
        </div>
      );
    };

    const renderSavedEnvVarSetsSection = (lockedView = false) => (
      <SavedEnvVarSetSelector
        getApiToken={getApiToken}
        selectedIds={savedEnvVarSetIds}
        setSelectedIds={setSavedEnvVarSetIds}
        readOnly={lockedView}
        usageLabel="backtest"
        onItemsLoaded={setSavedEnvVarSets}
      />
    );

    return (
      <div className="backtest-base-card">
        <div className="card-header">
          <div className="backtest-header-top">
            <h1>{cleanStrategyName}</h1>
            {linkedStrategyId && (
              <Badge bg="secondary" className="protected-strategy-badge ms-2" title="This is a protected strategy. Code remains with the author.">
                <FontAwesomeIcon icon={faLock} className="me-1" />
                Protected
              </Badge>
            )}
            <div className="backtest-version-meta">
              <div
                className="version-display"
                title={tradingAssetSummary ? `Trades: ${tradingAssetSummary}` : undefined}
              >
                Version {versionNumber}
              </div>
              {uniqueRequirementTokens.length > 0 && (
                <AssetPills
                  assets={uniqueRequirementTokens}
                  ariaLabel="Required data coverage"
                />
              )}
            </div>
          </div>
        </div>

        <div className="card-content">
          {/* --- NEW: Perplexity Warning --- */}
          {needsPerplexity && !isLoading && (
            <Alert variant="warning" className="backtest-page-perplexity-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Perplexity AI Detected:</strong> Backtesting strategies that rely heavily on real-time AI responses like Perplexity may not produce fully accurate historical results. Consider deploying for live paper trading to better evaluate performance.
            </Alert>
          )}

          {!isBacktestActive && !isLoading && renderDateRangeSection(false)}

          {isBacktestActive && (
            <div className="backtest-config-summary">
              <div className="config-summary-left">
                <FontAwesomeIcon icon={faTools} className="config-summary-icon" />
                <span className="config-summary-dates">
                  {formattedStartDate} → {formattedEndDate}
                </span>
              </div>
              <button
                type="button"
                className="config-summary-toggle"
                onClick={() => setShowRunConfigDetails((prev) => !prev)}
              >
                <FontAwesomeIcon icon={showRunConfigDetails ? faChevronUp : faChevronDown} className="me-1" />
                {showRunConfigDetails ? "Hide" : "Settings"}
              </button>
            </div>
          )}
          {isBacktestActive && showRunConfigDetails && (
            <div className="backtest-locked-config mt-3">
              {renderDateRangeSection(true)}
              <div className="mt-3">{renderProviderSection(true)}</div>
              <div className="mt-3">{renderSavedEnvVarSetsSection(true)}</div>
            </div>
          )}

          {/* Configuration section - contains Data Source and Environment Variables */}
          {!isBacktestActive && (
            <div className={`config-section ${configSectionCollapsed ? 'collapsed' : ''}`}>
              <div
                className="config-section__header"
                onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
              >
                <div className="config-section__title-group">
                  <FontAwesomeIcon icon={faCog} className="config-section__icon" />
                  <span className="config-section__title">Settings</span>
                  <span className="config-section__subtitle">(optional)</span>
                </div>
                <button className="config-section__toggle" type="button">
                  <FontAwesomeIcon icon={configSectionCollapsed ? faChevronDown : faChevronUp} />
                </button>
              </div>
              {/*
                IMPORTANT: Always render the content div so saved env-var metadata can load
                while the Settings panel is collapsed.
              */}
              <div className="config-section__content" style={{ display: configSectionCollapsed ? 'none' : undefined }}>
                {/* Data Source sub-section */}
                <div className="config-subsection">
                  <div className="config-subsection__label">Data Source</div>
                  <ProviderSelector
                    id="config-data-provider-select"
                    label=""
                    providers={providerOptionsForRender}
                    value={selectedProvider}
                    onChange={(next) => {
                      providerOverrideRef.current = true;
                      setSelectedProvider(next);
                    }}
                    loading={providerCatalogLoading}
                    error={providerCatalogError}
                    coverageWarning={providerCoverageWarningInfo}
                    disabled={false}
                  />
                </div>
                <div className="config-subsection">
                  <div className="config-subsection__label">Environment Variables</div>
                  {renderSavedEnvVarSetsSection(false)}
                </div>
                <div className="config-subsection">
                  <div className="config-subsection__label">Saved Env Var Sets</div>
                  {renderSavedEnvVarSetsSection(false)}
                </div>
              </div>
            </div>
          )}

          {renderBacktestStatus()}
          {!isLoading && renderActionButton()}

          {/* Action buttons section - Edit, Share, Start Trading - moved below Run Backtest */}
          <div className="backtest-action-buttons-row secondary">
            <StrategyActionButton
              actionType="editStrategy"
              size="compact"
              className="backtest-action-button"
              as={Link}
              to={`/ai-agent?aiStrategyId=${aiStrategyId}&revisionId=${revisionId}`}
              title="Edit this strategy"
              label="Edit Strategy"
            />
            <StrategyActionButton
              actionType="shareStrategy"
              size="compact"
              className="backtest-action-button"
              onClick={() => setShowShareModal(true)}
              title="Share this strategy"
              label="Share"
              type="button"
            />
            {baseStrategyId ? (
              <StrategyActionButton
                actionType="startTrading"
                size="compact"
                className="backtest-action-button"
                as={Link}
                to={
                  linkedStrategyId
                    ? `/deploy?linkedStrategyId=${encodeURIComponent(linkedStrategyId)}&aiStrategyId=${aiStrategyId}&aiStrategyRevisionId=${revisionId}`
                    : `/deploy?strategyId=${baseStrategyId}&aiStrategyId=${aiStrategyId}&aiStrategyRevisionId=${revisionId}`
                }
                title="Start live trading with your strategy"
                label="Start Trading"
              />
            ) : (
              <StrategyActionButton
                actionType="startTrading"
                size="compact"
                className="backtest-action-button backtest-action-button--disabled"
                title="Start live trading with your strategy"
                label="Start Trading"
                disabled
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  /** renderBacktestStatus function **/
  function renderBacktestStatus() {
    if (backtestStatus?.statusCode === "completed_no_trades" && !isLoading) {
      return (
        <div className="backtest-status-container">
          <Alert variant="info" className="backtest-status-message">
            <h3>
              <div className="backtest-result-icon info">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              Backtest Completed — No Trades
            </h3>
            <p className="status-message">
              {backtestStatus?.description || NO_TRADES_FALLBACK_MESSAGE}
            </p>
            <p className="status-message">
              Download the generated stats/logs below or refine the strategy so its entry rules can trigger within your selected dates.
            </p>
            <div className="error-actions text-center">
              <button
                className="btn btn-success btn-lg backtest-fix-link"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleFixNoTrades();
                }}
                title="Use AI to adjust this strategy for trade activity"
                style={{
                  padding: '0.75rem 2rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <FontAwesomeIcon icon={faTools} className="me-2" />
                Fix Using AI
              </button>
            </div>
          </Alert>
        </div>
      );
    }

    if (backtestStatus?.status === "Completed" && !isLoading) {
      return (
        <div className="backtest-status-container">
          <Alert variant="success" className="backtest-status-message">
            <h3>
              <div className="backtest-result-icon success">
                <FontAwesomeIcon icon={faCheck} />
              </div>
              Backtest Completed
            </h3>
            <p className="status-message">
              Your backtest completed successfully! Scroll down to view the results.
            </p>
          </Alert>
        </div>
      );
    }

    // Only show error if backtest is NOT actively running
    // This prevents file download errors or other transient errors from hiding the progress view
    if (error && backtestStatus?.status !== "Completed" && !backtestStatus?.running) {
      // Check if this is a connection issue or an actual backtest failure
      const isConnectionIssue = error === "connection_issue" || backtestStatus?.isConnectionError;

      return (
        <div className="backtest-status-container">
          <Alert variant={isConnectionIssue ? "warning" : "danger"} className="backtest-status-message">
            <h3>
              <div className={`backtest-result-icon ${isConnectionIssue ? 'warning' : 'error'}`}>
                <FontAwesomeIcon icon={isConnectionIssue ? faExclamationTriangle : faTimes} />
              </div>
              {isConnectionIssue ? "Connection Issue" : "Backtest Failed"}
            </h3>
            <p className="status-message">
              {isConnectionIssue
                ? "Temporary connection issue detected. Your backtest is still running on the server. The connection should restore automatically, or you can refresh the page."
                : error // Show the interpreted error for actual failures
              }
            </p>
            <div className="error-actions text-center">
              {isConnectionIssue ? (
                <button
                  className="btn btn-success btn-lg"
                  onClick={() => window.location.reload()}
                  title="Refresh the page to restore connection"
                  style={{
                    padding: '0.75rem 2rem',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  type="button"
                >
                  <FontAwesomeIcon icon={faSync} className="me-2" />
                  Refresh Page
                </button>
              ) : (
                <button
                  className="btn btn-success btn-lg backtest-fix-link"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFixError();
                  }}
                  title="Use AI to fix this backtest error"
                  style={{
                    padding: '0.75rem 2rem',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <FontAwesomeIcon icon={faTools} className="me-2" />
                  Fix Using AI
                </button>
              )}
            </div>
            {!isConnectionIssue && (
              <BacktestLogs
                backtestId={currentBacktestId}
                getApiToken={getApiToken}
                expanded={errorLogsExpanded}
                onToggle={handleErrorLogsToggle}
                className="status-logs"
              />
            )}
          </Alert>
        </div>
      );
    }

    if (isLoading) {
      // Construct backtest object for BacktestStatusCard
      // This reuses the unified BacktestStatusCard component (same as AI Agent page)
      // providing localStorage persistence for chart data and consistent UX
      const backtestForCard = {
        id: currentBacktestId,
        backtestId: currentBacktestId,
        status: backtestStatus?.status || backtestStatus?.statusCode,
        stage: backtestStatus?.stage,
        progress: backtestStatus?.progress,
        elapsed_ms: backtestStatus?.elapsed_ms,
        createdAt: backtestStatus?.createdAt,
      };

      // Wrapper for onStop - BacktestStatusCard passes backtestId but we use state
      const handleStopForCard = async (backtestId) => {
        await handleForceStop();
      };

      // Note: We don't pass chartData - let BacktestStatusCard accumulate its own
      // with localStorage persistence for survival across page refreshes
      return (
        <BacktestStatusCard
          backtest={backtestForCard}
          onStop={handleStopForCard}
          getApiToken={getApiToken}
          showLogs={true}
          className="backtest-page-status-card"
          startDate={startDate}
          endDate={endDate}
        />
      );
    }

    return null;
  }

  /** getRunDisabledReason function **/
  function getRunDisabledReason() {
    if (isLoading) return "Backtest is currently running";
    if (!startDate || !endDate) return "Please select a date range";
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) return "Start date must be before end date";
    // --- NEW: Check for missing required env vars ---
    if (requiredVarsStatus.missing.length > 0) {
      return `Missing required environment variable(s): ${requiredVarsStatus.missing.join(', ')}. Please provide value(s) above.`;
    }
    // --- Provider coverage check ---
    if (providerCoverageWarning) {
      if (
        selectedProvider === 'theta_data' &&
        (unsupportedRequirements.includes('crypto') || unsupportedRequirements.includes('futures'))
      ) {
        return `Theta Data does not support ${unsupportedRequirementSummary}. Switch to BotSpot Auto (recommended for crypto) or choose a compatible provider.`;
      }
      const providerName = selectedProviderInfo?.displayName || selectedProviderInfo?.name || 'Selected provider';
      return `${providerName} does not support ${unsupportedRequirementSummary}. Please select a compatible data provider.`;
    }
    return "";
  }

  /** renderActionButton function **/
  function renderActionButton() {
    // Don't show the Run button if we're loading OR if we have a connection issue
    if (isLoading || error === "connection_issue") {
      return null;
    }
    
    const disabledReason = getRunDisabledReason();
    
    return (
      <div className="run-backtest-container">
        <div className="button-tooltip-wrapper" {...(disabledReason ? { title: disabledReason } : {})}>
          <button 
            ref={backtestButtonRef}
            onClick={handleRunBacktest} 
            disabled={isRunDisabled}
            className="backtest-page-run-button"
            aria-label="Run backtest"
          >
            <FontAwesomeIcon icon={faCirclePlay} className="button-icon" />
            Run Backtest
          </button>
        </div>
        <BacktestStartAnimation 
          isVisible={showStartAnimation} 
          onAnimationComplete={() => setShowStartAnimation(false)} 
        />
      </div>
    );
  }

  // renderBacktestHistory - uses baseStrategyId for the table component props
  function renderBacktestHistory() {
    return (
      <div className="backtest-history-section">
        <BacktestTable
          botId={aiStrategyId} // Keep aiStrategyId if needed by table internally? Check component.
          strategyId={baseStrategyId} // Pass baseStrategyId for history fetching
          revisionId={revisionId}
          sortBy={sortBy}
          sortOrder={sortOrder}
          periodFilter={periodFilter}
          getApiToken={getApiToken}
          setError={setError}
          key={tableRefreshKey}
        />
      </div>
    );
  }

  // fetchVersionInfo - uses aiStrategyId and revisionId
  const fetchVersionInfo = useCallback(async () => {
    if (!aiStrategyId || !revisionId) {
      console.warn("[fetchVersionInfo] Missing required params:", { aiStrategyId, revisionId });
      return;
    }

    try {
      const token = await getApiToken();
      console.log("[fetchVersionInfo] Fetching versions for aiStrategyId:", aiStrategyId);
      
      const versionsData = await fetchWithAuth(token, `/ai-bot-builder/list-versions?aiStrategyId=${aiStrategyId}`);
      
      if (!versionsData?.versions?.length) {
        console.warn("[fetchVersionInfo] No versions found for strategy");
        return;
      }

      // Find the matching version using revisionId
      const matchingVersion = versionsData.versions.find(v => v.revisionId === revisionId);
      
      if (matchingVersion) {
        console.log("[fetchVersionInfo] Found matching version:", matchingVersion.version);
        setVersionNumber(matchingVersion.version);
      } else {
        console.warn("[fetchVersionInfo] No matching version found for revisionId:", revisionId);
      }
    } catch (err) {
      console.error("[fetchVersionInfo] Error:", err);
      setError(interpretErrorCode(err.message || "ERROR_CODE_FETCH_VERSION_INFO"));
    }
  }, [aiStrategyId, revisionId, getApiToken]);

  // --- NEW: Effect to fetch baseStrategyId (or linked strategy info) ---
  useEffect(() => {
    let isMounted = true;

    // Handle LinkedStrategy mode - fetch info from linked-strategies endpoint
    const fetchLinkedStrategyInfo = async () => {
      setIsInitialSetupLoading(true);
      setBaseStrategyIdError("");
      try {
        const token = await getApiToken();
        console.log("[fetchLinkedStrategyInfo] Fetching linked strategy:", linkedStrategyId);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/linked-strategies/${linkedStrategyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          // Handle expired marketplace purchase — redirect to strategy detail page
          if (response.status === 403) {
            try {
              const errData = await response.json();
              if (errData.code === "PURCHASE_REQUIRED" && errData.listingId) {
                console.log("[fetchLinkedStrategyInfo] Purchase expired, redirecting to marketplace listing:", errData.listingId);
                navigate(`/marketplace/strategy/${errData.listingId}`);
                return;
              }
            } catch { /* fall through to generic error */ }
          }
          throw new Error(`Failed to fetch linked strategy: ${response.status}`);
        }

        const linkedData = await response.json();

        if (isMounted) {
          // For linked strategies, set the original strategy's base ID
          if (linkedData.originalStrategy?.id) {
            setBaseStrategyId(linkedData.originalStrategy.id);
          }
          // Set strategy name and other info
          setStrategyName(linkedData.name || linkedData.originalStrategy?.name || "Linked Strategy");
          setCurrentStrategy({
            id: linkedData.originalStrategy?.id,
            name: linkedData.name || linkedData.originalStrategy?.name,
            isLinked: true,
            linkedStrategyId: linkedStrategyId,
          });
          // For linked strategies, code stays with author - set placeholder
          setStrategyCode("# Protected strategy - code is managed by the author\n# Backtesting will use the author's code securely on the server");
          setIsInitialSetupLoading(false);
        }
      } catch (err) {
        console.error("[fetchLinkedStrategyInfo] Error:", err);
        if (isMounted) {
          setBaseStrategyIdError(interpretErrorCode(err.message || "ERROR_CODE_FETCH_LINKED_STRATEGY_FAILED"));
          setIsInitialSetupLoading(false);
        }
      }
    };

    const fetchBaseId = async () => {
      if (!aiStrategyId) {
        setBaseStrategyIdError("Missing AI Strategy ID");
        setIsInitialSetupLoading(false); // Stop loading on error
        return;
      }
      setIsInitialSetupLoading(true); // Ensure loading state is true
      setBaseStrategyIdError(""); // Clear previous error
      try {
        const token = await getApiToken();
        console.log("[fetchBaseId] Fetching AI strategies list...");
        const aiStrategiesRes = await fetchWithAuth(token, `/ai-bot-builder/list-strategies`);
        const aiStrategy = aiStrategiesRes?.aiStrategies?.find(s => s.id === aiStrategyId);

        if (aiStrategy?.strategy?.id) {
          if (isMounted) {
            console.log("[fetchBaseId] Found base strategy ID:", aiStrategy.strategy.id);
            setBaseStrategyId(aiStrategy.strategy.id);
            // --- Stop initial setup loading on success ---
            setIsInitialSetupLoading(false);
          }
        } else {
          throw new Error("Base strategy ID not found for the given AI strategy ID.");
        }
      } catch (err) {
        console.error("[fetchBaseId] Error:", err);
        if (isMounted) {
          setBaseStrategyIdError(interpretErrorCode(err.message || "ERROR_CODE_FETCH_BASE_ID_FAILED"));
          // --- Stop initial setup loading on error ---
          setIsInitialSetupLoading(false);
        }
      }
    };

    // Choose which fetch to run based on linkedStrategyId presence
    if (linkedStrategyId) {
      fetchLinkedStrategyInfo();
    } else {
      fetchBaseId();
    }
    return () => { isMounted = false; };
  }, [aiStrategyId, linkedStrategyId, getApiToken]);
  // ---

  // Update main useEffect to depend on baseStrategyId before fetching code/version/history
  useEffect(() => {
    // --- Wait for initial setup (baseId fetch) to complete ---
    if (isInitialSetupLoading) {
      console.log("[Main Effect] Waiting for initial setup (baseStrategyId fetch)...");
      return;
    }
    // ---

    // LinkedStrategy mode - code and version are handled in fetchLinkedStrategyInfo
    // Only need to initialize backtest state
    if (linkedStrategyId && baseStrategyId && !baseStrategyIdError) {
      console.log("[Main Effect] LinkedStrategy mode - initializing backtest state.");
      initializeBacktestState();
      return;
    }

    // Only proceed if we have all required IDs and no errors
    if (aiStrategyId && revisionId && baseStrategyId && !baseStrategyIdError) {
      console.log("[Main Effect] All IDs available, fetching code, version, and initializing state.");
      fetchStrategyCode();
      fetchVersionInfo();
      initializeBacktestState(); // This now also depends on baseStrategyId internally
    } else {
      console.log("[Main Effect] Waiting for all required IDs or error occurred:", { aiStrategyId, revisionId, baseStrategyId, baseStrategyIdError });
    }
    // Add baseStrategyId and isInitialSetupLoading to dependency array
  }, [aiStrategyId, revisionId, baseStrategyId, baseStrategyIdError, isInitialSetupLoading, linkedStrategyId, fetchStrategyCode, fetchVersionInfo, initializeBacktestState]);

  // Fix the detection logic by using localStorage to track in-progress backtests
  // ... (ensure this effect also waits for baseStrategyId if detectInProgressBacktest depends on it) ...
  useEffect(() => {
    let isMounted = true;
    const checkForInProgressBacktest = async () => {
      // --- Wait for baseStrategyId ---
      if (!aiStrategyId || !baseStrategyId || !isMounted || recoveryAttempted) return;
      // ---
      try {
        console.log("Checking for in-progress backtests...");
        const recovered = await detectInProgressBacktest();
        
        if (!isMounted) return;
        
        // Mark that we've attempted recovery to prevent future checks
        setRecoveryAttempted(true);
        
        // Set loading state based on recovery result
        setIsLoading(!!recovered);
        
        // Only fetch history once on recovery
        if (recovered && !isLoadingHistory) {
          await fetchBacktestHistory();
        }
      } catch (error) {
        console.error("Error checking for in-progress backtests:", error);
        setRecoveryAttempted(true); // Still mark as attempted on error
      }
    };
    
    // Check on initial load only if we haven't attempted recovery
    if (!recoveryAttempted) {
      checkForInProgressBacktest();
    }
    
    // Re-check when the window regains focus, but only if not already polling and not attempted recovery
    const handleFocus = () => {
      if (!pollingRef.current && !recoveryAttempted) {
        checkForInProgressBacktest();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      isMounted = false;
      window.removeEventListener('focus', handleFocus);
      clearInterval(timerIntervalRef.current);
      clearInterval(pollingRef.current);
    };
  }, [aiStrategyId, baseStrategyId, detectInProgressBacktest, fetchBacktestHistory, isLoadingHistory, recoveryAttempted]);

  // Main render
  return (
    <div className="backtest-page">
      {/* --- Show loading spinner during initial baseStrategyId fetch --- */}
      {isInitialSetupLoading ? (
        // --- Use namespaced class ---
        <div className="backtest-page-loading-container">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading strategy details...</p>
        </div>
      ) : requiredParamsError ? (
        /* --- Show error only if actual error occurred (not during loading) --- */
        <Alert variant="danger" className="m-4">{requiredParamsError}</Alert>
      ) : (
        /* --- Render main content only when loading is done and no errors --- */
        <div className="backtest-container">
          {checkoutStatus === "success" && (
            <Alert variant="success" className="backtest-checkout-alert">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                <div>
                  <div className="fw-semibold">Purchase complete.</div>
                  <div className="small mt-1">
                    {purchasedProductNames.length
                      ? `Access granted for ${purchasedProductNames.join(", ")}. `
                      : ""}
                    Your backtest will start automatically once access is ready.
                  </div>
                </div>
                <Link
                  className="backtest-guardrail-button backtest-guardrail-button--outline backtest-checkout-alert__manage-button"
                  to="/account-settings/subscriptions"
                >
                  Manage Subscriptions
                </Link>
              </div>
            </Alert>
          )}
          {renderTopCard()}
          {/* Only render history if baseStrategyId is available */}
          {baseStrategyId && renderBacktestHistory()}
        </div>
      )}

      <ContentModal
        isOpen={showTrialModal}
        onClose={handleCloseTrialModal}
        title="Start Your Theta Data Trial"
        size="sm"
        footer={(
          <div className="d-flex justify-content-center gap-2 w-100">
            <button
              type="button"
              className="trial-run-button"
              onClick={handleStartThetaTrial}
              disabled={trialRequestPending}
            >
              {trialRequestPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Starting…
                </>
              ) : (
                'Start Trial & Run Backtest'
              )}
            </button>
          </div>
        )}
      >
        <p className="mb-2">
          To run this backtest, you'll need Theta Data access for:
        </p>
        <ul className="mb-3">
          {pendingTrialProducts.map((product) => (
            <li key={product.productSlug}>{product.displayName}</li>
          ))}
        </ul>
        <p className="text-muted small mb-3">
          Your free trial includes 30 days of access and up to 1 year of historical data for each selected asset class.
        </p>
        {trialModalError && (
          <Alert variant="danger">{trialModalError}</Alert>
        )}
      </ContentModal>

      <ContentModal
        isOpen={showGuardrailModal}
        onClose={handleCloseGuardrailModal}
        title="Extend Your Theta Data Access"
        size="sm"
        footer={(
          <div className="d-flex justify-content-center flex-wrap gap-3 w-100 backtest-guardrail-actions">
            <button
              type="button"
              className="backtest-guardrail-button backtest-guardrail-button--outline"
              onClick={handleAdjustDatesAndRun}
            >
              Adjust Dates &amp; Run Backtest
            </button>
            <button
              type="button"
              className="backtest-guardrail-button backtest-guardrail-button--primary"
              onClick={handleOpenPlansFromGuardrail}
            >
              View Plans
            </button>
          </div>
        )}
      >
        <p className="mb-2">
          Theta Data trials only allow start dates within the last {guardrailDetails?.limitDays || THETA_TRIAL_LOOKBACK_DAYS} days. Adjust your backtest or upgrade your plan for deeper history.
        </p>
        {guardrailDetails?.products?.length ? (
          <ul className="mb-2">
            {guardrailDetails.products.map((product) => (
              <li key={product.productSlug}>
                {product.displayName} – trial limit {product.defaultTrialLookbackDays || 365} days
              </li>
            ))}
          </ul>
        ) : null}
        {guardrailDetails?.startDaysAgo != null && (
          <p className="text-muted small mb-1">
            Current start date: {guardrailDetails.startDaysAgo} days ago (limit {guardrailDetails?.limitDays || THETA_TRIAL_LOOKBACK_DAYS} days)
          </p>
        )}
      </ContentModal>

      <ContentModal
        isOpen={showPlansModal}
        onClose={handleClosePlansModal}
        title="Theta Data Plans"
        size="md"
        footer={(
          <div className="d-flex justify-content-center flex-wrap gap-3 w-100 backtest-guardrail-actions">
            <button
              type="button"
              className="backtest-guardrail-button backtest-guardrail-button--outline"
              onClick={handleClosePlansModal}
            >
              Close
            </button>
            <button
              type="button"
              className="backtest-guardrail-button backtest-guardrail-button--primary"
              onClick={handleCheckout}
              disabled={
                cartPending ||
                selectedPlanCount === 0 ||
                Boolean(thetaCatalogBlockingError)
              }
            >
              {cartPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Preparing…
                </>
              ) : (
                'Continue to Checkout'
              )}
            </button>
          </div>
        )}
      >
        {checkoutError && (
          <Alert variant="danger" className="mb-3">
            {checkoutError}
          </Alert>
        )}
        {!checkoutError && cartMutationError?.message && (
          <Alert variant="danger" className="mb-3">
            {cartMutationError.message}
          </Alert>
        )}
        {catalogLoadError && (
          <Alert variant="warning" className="mb-3">
            Unable to load latest pricing details. Showing cached data.
          </Alert>
        )}
        {thetaCatalogBlockingError && (
          <Alert variant="danger" className="mb-3">
            <div>{thetaCatalogBlockingError}</div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                variant="outline-light"
                size="sm"
                onClick={refreshCatalog}
                disabled={catalogLoading}
              >
                {catalogLoading ? 'Retrying…' : 'Retry' }
              </Button>
            </div>
          </Alert>
        )}
        {catalogLoading && (
          <div className="text-center my-3">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            Loading catalog…
          </div>
        )}
        {!thetaCatalogBlockingError && thetaPlanEntries.length ? (
          <>
            <p className="text-muted mb-3">
              Trials include the last 1 year of data for 30 days—upgrading keeps the full feed available for any date range.
            </p>
            <div className="theta-plan-list">
              {thetaPlanEntries.map((entry) => {
                const upgradeCopy = (() => {
                  switch (entry.productSlug) {
                    case "theta-stocks":
                      return "Upgrade for 12 years of tick-level US and OTC history beyond the 1-year trial limit.";
                    case "theta-options":
                      return "Upgrade to keep the full US options stream past the 1-year trial window.";
                    case "theta-index":
                      return "Upgrade to unlock 7+ years of SPX/VIX history beyond the 1-year trial window.";
                    case "theta-bundle":
                      return "Get complete stocks, options, and indexes data with no 30-day cutoff.";
                    default:
                      return null;
                  }
                })();

              const {
                productSlug,
                displayName,
                capabilitySummary,
                statusMeta,
                isRequired,
                isConflict,
                isSelected,
                isDisabled,
                trialNote,
                priceLabel,
                defaultPlan,
                disabledReason,
                isIncludedViaBundle,
                bundleInfo,
              } = entry;

              const badgeNodes = [];
              if (isRequired) {
                badgeNodes.push(
                  <Badge bg="info" className="theta-plan-item__badge" key="required">
                    Required for this backtest
                  </Badge>
                );
              }
              if (isSelected) {
                badgeNodes.push(
                  <Badge bg="primary" className="theta-plan-item__badge" key="selected">
                    Selected
                  </Badge>
                );
              }
              if (isIncludedViaBundle && bundleInfo) {
                badgeNodes.push(
                  <Badge bg="secondary" className="theta-plan-item__badge" key="bundle">
                    Included via {bundleInfo.displayName}
                  </Badge>
                );
              }

              const cardClasses = clsx("theta-plan-item", {
                "theta-plan-item--selected": isSelected,
                "theta-plan-item--required": isRequired,
                "theta-plan-item--conflict": isConflict,
                "theta-plan-item--trial-active": Boolean(trialNote),
                "theta-plan-item--disabled": isDisabled,
                "theta-plan-item--bundle-included": isIncludedViaBundle,
              });

              const planLabel = defaultPlan?.label || defaultPlan?.planKey || null;
              const showPrice = Boolean(priceLabel) && priceLabel !== planLabel;

              return (
                <div
                  key={productSlug}
                  className={cardClasses}
                  role={isDisabled ? undefined : "button"}
                  tabIndex={isDisabled ? -1 : 0}
                  aria-pressed={isSelected}
                  aria-disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      handleTogglePlan(productSlug);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (isDisabled) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleTogglePlan(productSlug);
                    }
                  }}
                >
                  <div className="theta-plan-item__header">
                    <div className="theta-plan-item__title-block">
                      <div className="theta-plan-item__title-row">
                        <h5 className="theta-plan-item__title">
                          {displayName}
                        </h5>
                        {badgeNodes.length > 0 && (
                          <div className="theta-plan-item__badge-group">
                            {badgeNodes}
                          </div>
                        )}
                      </div>
                      {planLabel && (
                        <div className="theta-plan-item__meta">
                          Plan: {planLabel}
                        </div>
                      )}
                      {showPrice && (
                        <div className="theta-plan-item__price">
                          {priceLabel}
                        </div>
                      )}
                      <div className="theta-plan-item__meta">
                        Capabilities: {capabilitySummary}
                      </div>
                      {upgradeCopy ? (
                        <div className="theta-plan-item__meta theta-plan-item__upgrade-copy">
                          {upgradeCopy}
                        </div>
                      ) : null}
                      {isIncludedViaBundle && bundleInfo ? (
                        <div className="theta-plan-item__meta theta-plan-item__bundle-note">
                          Included via {bundleInfo.displayName}. Select to switch to this plan only.
                        </div>
                      ) : null}
                      {disabledReason && (
                        <div className="theta-plan-item__disabled-note">
                          {disabledReason}
                        </div>
                      )}
                    </div>
                    <Badge bg={statusMeta.variant} className="theta-plan-item__status-badge">
                      {statusMeta.label}
                    </Badge>
                  </div>
                  {trialNote && (
                    <div className="theta-plan-item__trial-note">
                      {trialNote}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <p className="text-muted">{catalogLoading ? 'Loading Theta Data plans…' : 'No Theta Data plans available yet.'}</p>
        )}
        {!catalogLoading && thetaPlanEntries.length > 0 && selectedPlanCount === 0 && (
          <p className="text-muted small mt-3 mb-0">
            Select at least one plan to continue to checkout.
          </p>
        )}
      </ContentModal>

      {/* Share Strategy Modal */}
      {aiStrategyId && revisionId && (
        <ShareStrategyModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          aiStrategyId={aiStrategyId}
          revisionId={revisionId}
          strategyName={cleanStrategyName || 'Strategy'}
          isPublic={isStrategyPublic}
          isInMarketplace={false}
          shareUrl={`${window.location.origin}/ai-bot-builder/${aiStrategyId}?revisionId=${revisionId}`}
          onMakePublic={handleShareStrategy}
          getAuthHeaders={async () => ({
            'Authorization': `Bearer ${await getApiToken()}`
          })}
        />
      )}

      {/* PaywallModal for backtest limit reached */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        limitType={paywallData?.limitType}
        usage={paywallData?.usage}
        checkoutUrl={paywallData?.checkoutUrl}
      />
    </div>
  );
}
